import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import config from '../runtime-config';
import createControlApi from '../services/controlApi';
import { nodeIdentity, monitorEventMatches, pollingRuntime, mergeNodeRefresh, createRefreshGate } from '../services/nodeState';
import CatalogFilter, { filterCatalogItems } from './CatalogFilter';
import ManifestPicker from './ManifestPicker';
import FabricContractEditor from './FabricContractEditor';
import UserActionsSelect, { confirmUserAction, groupPresentation } from './UserActionsSelect';
import ManagedUserStatusIcon from './ManagedUserStatusIcon';
import ModulePorts, { modulePortDraft, portPayload } from './ModulePorts';
import { savedModuleOverrides } from '../services/moduleOverrides';
import './Dashboard.css';

const CONTROL_API = createControlApi(config.aws.apiEndpoint);

const SERVICES = [
  {
    id: 'datahub',
    serviceKey: 'datahub',
    name: 'Data Hub',
    description: 'Inspect routes, sources, and live device data.',
    port: '8081',
    session: (nodeId) => `${nodeId}-datahub`,
    mark: 'DH',
    available: true
  },
  {
    id: 'configsync',
    serviceKey: 'configsync',
    name: 'ConfigSync',
    description: 'Review synchronized configuration and module logs.',
    port: '8088',
    session: (nodeId) => `${nodeId}-configsync`,
    mark: 'CS',
    available: true
  },
  {
    id: 'frpc',
    serviceKey: 'adminpanel',
    name: 'Tunnel Admin',
    description: 'See active FRP proxies and tunnel health.',
    port: '7400',
    session: (nodeId) => `${nodeId}-adminpanel`,
    mark: 'FR',
    available: true
  },
  {
    id: 'video',
    serviceKey: 'video',
    name: 'Video Manager',
    description: 'Manage camera feeds and inference pipelines.',
    port: '8090',
    session: (nodeId) => `${nodeId}-video`,
    mark: 'VM',
    available: true
  },
  {
    id: 'grafana',
    serviceKey: 'grafana',
    name: 'Grafana',
    description: 'Explore node health, telemetry, and dashboards.',
    port: '3000',
    session: (nodeId) => `${nodeId}-grafana`,
    mark: 'GF',
    available: true
  },
  {
    id: 'influxdb',
    serviceKey: 'influxdb',
    name: 'InfluxDB',
    description: 'Open the InfluxDB data explorer and administration UI.',
    port: '8086',
    session: (nodeId) => `${nodeId}-influxdb`,
    mark: 'IDB',
    available: true
  },
  {
    id: 'mini-top',
    serviceKey: 'mini-top',
    name: 'Mini Top',
    description: 'Inspect live node metrics and system details.',
    port: '9100',
    session: (nodeId) => `${nodeId}-mini-top`,
    mark: 'MT',
    available: true
  },
  {
    id: 'fleet-gateway',
    serviceKey: 'fleet-gateway',
    name: 'Fleet Gateway',
    description: 'View fleet locations and recent position updates.',
    port: '8091',
    session: (nodeId) => `${nodeId}-fleet-gateway`,
    mark: 'FG',
    available: true
  }
];

const EMPTY_MANIFEST_CATALOG = { presets: [], modules: [] };
// These modules are present in every generated base manifest and cannot be removed.
const REQUIRED_BASE_MODULE_IDS = ['ConfigSync', 'frpc', 'module-monitor'];
// Mini Top is included by default, but unlike the required base modules it is optional.
const DEFAULT_CUSTOM_MODULE_IDS = [...REQUIRED_BASE_MODULE_IDS, 'mini-top'];

const NON_SHAREABLE_SERVICE_KEYS = new Set(['configsync', 'module-monitor']);
const isShareableServiceKey = (serviceKey) => !NON_SHAREABLE_SERVICE_KEYS.has(String(serviceKey || '').trim().toLowerCase());
const isUserLaunchableServiceKey = (serviceKey) => String(serviceKey || '').trim().toLowerCase() !== 'module-monitor';

const normalizeList = (value) => (
  Array.isArray(value)
    ? value
    : (value || '').toString().split(',').map((item) => item.trim()).filter(Boolean)
);

const displayRuntimeStatus = (node, moduleItem) => {
  const runtime = (node.runtimeModules || []).find((item) => item.id === moduleItem.id);
  if (runtime?.status) {
    return runtime.status.replace(/(^|[-_\s])(\w)/g, (_match, prefix, character) => `${prefix}${character.toUpperCase()}`);
  }
  if (node.runtimeStatusAvailable) {
    return 'Not reported';
  }
  return node.status !== 'online' ? 'Offline' : 'Status unavailable';
};

const monitorRuntimeModule = (moduleItem) => ({
  id: moduleItem.moduleId,
  status: String(moduleItem.effectiveStatus || moduleItem.runtimeStatus || 'unknown').toLowerCase(),
  description: moduleItem.description || ''
});

const mergeMonitorRuntimeModules = (runtimeModules, updates) => {
  const byId = new Map((runtimeModules || []).map((moduleItem) => [moduleItem.id, moduleItem]));
  for (const update of updates) {
    if (!update?.id) continue;
    byId.set(update.id, { ...(byId.get(update.id) || {}), ...update });
  }
  return [...byId.values()];
};

const normalizeImageTags = (value) => {
  if (!value) {
    return {};
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value;
  }
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const imageTagFromReference = (image) => {
  const reference = String(image || '').split('@')[0];
  const slashIndex = reference.lastIndexOf('/');
  const tagIndex = reference.lastIndexOf(':');
  return tagIndex > slashIndex ? reference.slice(tagIndex + 1) : '';
};

const catalogModuleTag = (moduleItem) => (
  imageTagFromReference(moduleItem?.deployment?.settings?.image)
  || moduleItem?.defaultTag
  || ''
);

const catalogModuleImageRepository = (moduleItem) => {
  const image = String(moduleItem?.deployment?.settings?.image || '').split('@')[0];
  const repository = image.includes('/') ? image.slice(image.indexOf('/') + 1) : image;
  const tagIndex = repository.lastIndexOf(':');
  return tagIndex >= 0 ? repository.slice(0, tagIndex) : repository;
};

const catalogModuleUsesAcr = (moduleItem) => /^[^/]+\.azurecr\.io\//i.test(
  String(moduleItem?.deployment?.settings?.image || '')
);

const SESSION_TIMEOUT_OPTIONS = [
  ['2h', '2 hours'], ['4h', '4 hours'], ['6h', '6 hours'], ['8h', '8 hours'],
  ['16h', '16 hours'], ['24h', '24 hours'], ['36h', '36 hours'],
  ['1wk', '1 week'], ['infinity', 'Never expires']
];

const availableCatalogImageTags = (catalogImages, order) => {
  const tags = (catalogImages?.digests || []).flatMap((manifest) => (manifest.tags || []).map((tag) => ({
    tag,
    updatedAt: manifest.updatedAt || manifest.createdAt || ''
  })));
  return tags.sort((left, right) => {
    if (order === 'tag') return left.tag.localeCompare(right.tag);
    const dateOrder = (Date.parse(left.updatedAt) || 0) - (Date.parse(right.updatedAt) || 0);
    return (order === 'oldest' ? dateOrder : -dateOrder) || left.tag.localeCompare(right.tag);
  });
};

const miniTopNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const miniTopPercent = (value) => `${miniTopNumber(value).toFixed(1)}%`;

const miniTopRate = (bytesPerSecond) => {
  const value = miniTopNumber(bytesPerSecond);
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB/s`;
  if (value >= 1024) return `${(value / 1024).toFixed(1)} KB/s`;
  return `${value.toFixed(0)} B/s`;
};

const MiniTopMetricCard = ({ title, value, detail, percent, active, onClick }) => {
  const clickable = typeof onClick === 'function';
  const content = (
    <>
      <strong>{value}</strong>
      <span>{title}</span>
      {percent !== undefined && (
        <i className="mini-top-meter" aria-hidden="true"><b style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} /></i>
      )}
      {detail && <small>{detail}</small>}
    </>
  );
  return clickable ? (
    <button type="button" className={`mini-top-card ${active ? 'active' : ''}`} onClick={onClick}>{content}</button>
  ) : (
    <div className="mini-top-card">{content}</div>
  );
};

const MiniTopMetrics = ({ snapshot, activeDetail, onSelectDetail }) => {
  const cpu = snapshot.cpu && typeof snapshot.cpu === 'object' ? snapshot.cpu : {};
  const memory = snapshot.mem && typeof snapshot.mem === 'object' ? snapshot.mem : {};
  const rootFs = snapshot.fs?.root && typeof snapshot.fs.root === 'object' ? snapshot.fs.root : {};
  const net = snapshot.net && typeof snapshot.net === 'object' && !Array.isArray(snapshot.net) ? snapshot.net : {};
  const cpuBusy = Object.keys(cpu).length ? Math.max(0, 100 - miniTopNumber(cpu.id, 100)) : null;
  const memoryTotal = miniTopNumber(memory.total_mib);
  const memoryUsed = miniTopNumber(memory.used_mib);
  const memoryPercent = memoryTotal ? (memoryUsed / memoryTotal) * 100 : 0;
  const diskTotal = miniTopNumber(rootFs.total_gb);
  const diskUsed = miniTopNumber(rootFs.used_gb);
  const diskPercent = diskTotal ? (diskUsed / diskTotal) * 100 : 0;
  const interfaces = Object.entries(net).filter(([, value]) => value && typeof value === 'object');
  const receiveRate = interfaces.reduce((total, [, value]) => total + miniTopNumber(value.rx_bytes_s), 0);
  const transmitRate = interfaces.reduce((total, [, value]) => total + miniTopNumber(value.tx_bytes_s), 0);
  const cpuExtra = snapshot.cpu_extra && typeof snapshot.cpu_extra === 'object' ? snapshot.cpu_extra : {};
  const frequencies = Array.isArray(cpuExtra.freq_mhz_per_core) ? cpuExtra.freq_mhz_per_core.map(miniTopNumber) : [];
  const frequencyText = frequencies.length
    ? `${Math.min(...frequencies).toFixed(0)} / ${(frequencies.reduce((sum, value) => sum + value, 0) / frequencies.length).toFixed(0)} / ${Math.max(...frequencies).toFixed(0)} MHz`
    : 'Not reported';
  const detailPanels = {
    cpu: <>
      <div className="mini-top-detail-row"><span>User {miniTopPercent(cpu.us)}</span><span>System {miniTopPercent(cpu.sy)}</span><span>Idle {miniTopPercent(cpu.id)}</span></div>
      <div className="mini-top-detail-row"><span>Load {miniTopNumber(snapshot.load?.['1']).toFixed(2)} / {miniTopNumber(snapshot.load?.['5']).toFixed(2)} / {miniTopNumber(snapshot.load?.['15']).toFixed(2)}</span><span>Freq {frequencyText}</span><span>Gov {typeof cpuExtra.governor === 'string' ? cpuExtra.governor : '—'}</span></div>
    </>,
    mem: <>
      <div className="mini-top-detail-row"><span>Avail {miniTopNumber(memory.avail_mib).toFixed(0)} MiB</span><span>Free {miniTopNumber(memory.free_mib).toFixed(0)} MiB</span><span>Buff/Cache {miniTopNumber(memory.buff_cache_mib).toFixed(0)} MiB</span></div>
      <div className="mini-top-detail-row"><span>Swap used {miniTopNumber(snapshot.swap?.used_mib).toFixed(0)} MiB</span><span>Tasks {miniTopNumber(snapshot.tasks?.total).toFixed(0)}</span><span>Users {miniTopNumber(snapshot.users).toFixed(0)}</span></div>
    </>,
    net: <div className="mini-top-net-detail">{interfaces.length ? interfaces.map(([name, stats]) => (
      <div key={name}><strong>{name}</strong><span>↓ {miniTopRate(stats.rx_bytes_s)}</span><span>↑ {miniTopRate(stats.tx_bytes_s)}</span><small>{stats.state || 'unknown'}{stats.speed_mbps ? ` · ${stats.speed_mbps} Mbps` : ''}</small></div>
    )) : <span>No interface-rate sample yet.</span>}</div>
  };

  return (
    <div className="mini-top-dashboard">
      <div className="mini-top-cards">
        <MiniTopMetricCard title="CPU" value={cpuBusy === null ? '—' : miniTopPercent(cpuBusy)} active={activeDetail === 'cpu'} onClick={() => onSelectDetail('cpu')} />
        <MiniTopMetricCard title="Mem" value={memoryTotal ? miniTopPercent(memoryPercent) : '—'} percent={memoryPercent} active={activeDetail === 'mem'} onClick={() => onSelectDetail('mem')} />
        <MiniTopMetricCard title="Disk" value={diskTotal ? miniTopPercent(diskPercent) : '—'} percent={diskPercent} />
        <MiniTopMetricCard title="Net" value={`${miniTopRate(receiveRate)} / ${miniTopRate(transmitRate)}`} active={activeDetail === 'net'} onClick={() => onSelectDetail('net')} />
        <MiniTopMetricCard title="Uptime" value={snapshot.uptime?.human || '—'} />
      </div>
      <div className="mini-top-detail" aria-live="polite">{detailPanels[activeDetail] || detailPanels.cpu}</div>
    </div>
  );
};

const MiniTopHeaderSummary = ({ snapshot }) => {
  const cpu = snapshot?.cpu && typeof snapshot.cpu === 'object' ? snapshot.cpu : {};
  const memory = snapshot?.mem && typeof snapshot.mem === 'object' ? snapshot.mem : {};
  const memoryTotal = miniTopNumber(memory.total_mib);
  const memoryPercent = memoryTotal ? (miniTopNumber(memory.used_mib) / memoryTotal) * 100 : null;
  const cpuPercent = Object.keys(cpu).length ? Math.max(0, 100 - miniTopNumber(cpu.id, 100)) : null;
  const load = snapshot?.load?.['1'];
  return (
    <div className="mini-top-header-summary" aria-label="Mini Top summary">
      <span><em>CPU</em><strong>{cpuPercent === null ? '—' : miniTopPercent(cpuPercent)}</strong></span>
      <span><em>Mem</em><strong>{memoryPercent === null ? '—' : miniTopPercent(memoryPercent)}</strong></span>
      <span><em>Load</em><strong>{load === undefined ? '—' : miniTopNumber(load).toFixed(2)}</strong></span>
    </div>
  );
};

const sortTagSettings = (modules) => [...(modules || [])].sort((left, right) => (
  (left.name || left.id || '').localeCompare(right.name || right.id || '', undefined, { sensitivity: 'base' })
));

const sameService = (left, right) => String(left || '').toLowerCase() === String(right || '').toLowerCase();
const nodeHasModuleMonitor = (node) => Boolean(node && (
  normalizeList(node.allowedServices).some((service) => sameService(service, 'module-monitor'))
  || (node.deployedModules || []).some((moduleItem) => (
    (typeof moduleItem === 'string' ? moduleItem : moduleItem.id) === 'module-monitor'
  ))
));

const deriveModuleInventory = ({ catalog, preset = 'base', modules = [] }) => {
  const moduleMap = new Map(catalog.modules.map((moduleItem) => [moduleItem.id, moduleItem]));
  const presetMap = new Map(catalog.presets.map((presetItem) => [presetItem.id, presetItem]));
  const moduleIds = new Set(REQUIRED_BASE_MODULE_IDS);
  for (const moduleId of presetMap.get(preset)?.modules || []) {
    moduleIds.add(moduleId);
  }
  for (const selectedModule of normalizeList(modules)) {
    const metadata = moduleMap.get(selectedModule);
    for (const moduleId of metadata?.modules || [selectedModule]) {
      moduleIds.add(moduleId);
    }
  }
  return [...moduleIds].map((moduleId) => ({
    id: moduleId,
    name: moduleMap.get(moduleId)?.name || moduleId,
    description: moduleMap.get(moduleId)?.description || '',
    browserOpenable: moduleMap.get(moduleId)?.browserOpenable === true,
    serviceKey: moduleMap.get(moduleId)?.serviceKey || '',
    port: moduleMap.get(moduleId)?.hostPort ?? moduleMap.get(moduleId)?.port ?? '',
    services: moduleMap.get(moduleId)?.services || []
  }));
};

const manifestModuleSummary = ({ catalog, preset, modules }) => {
  const presetItem = catalog.presets.find((item) => item.id === preset);
  const moduleMap = new Map(catalog.modules.map((moduleItem) => [moduleItem.id, moduleItem]));
  const moduleIds = preset === 'custom'
    ? normalizeList(modules)
    : (presetItem?.modules || []);

  return moduleIds.map((moduleId) => {
    const moduleItem = moduleMap.get(moduleId) || {};
    return {
      id: moduleId,
      name: moduleItem.name || moduleId,
      browserOpenable: moduleItem.browserOpenable === true,
      serviceKey: moduleItem.serviceKey || '',
      port: moduleItem.hostPort ?? moduleItem.port ?? ''
    };
  });
};

const localDeploymentTimestamp = (deploymentDate, deploymentTime) => {
  if (!deploymentDate || !deploymentTime) {
    return deploymentDate || '';
  }

  const match = deploymentDate.match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
  if (!match) {
    return `${deploymentDate} ${deploymentTime}`;
  }

  const [, day, month, year] = match;
  const timestamp = new Date(`20${year}-${month}-${day}T${deploymentTime}Z`);
  if (Number.isNaN(timestamp.getTime())) {
    return `${deploymentDate} ${deploymentTime}`;
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).format(timestamp).replace(',', '');
};

const localDeploymentTimestampFromIso = (timestamp) => {
  if (!timestamp) return '';
  const value = new Date(timestamp);
  if (Number.isNaN(value.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).format(value).replace(',', '');
};

const INSTANCE_SPECS = {
  't3a.medium': { vcpu: 2, memory: '4 GiB' },
  't3a.large': { vcpu: 2, memory: '8 GiB' },
  't3a.xlarge': { vcpu: 4, memory: '16 GiB' },
  't3a.2xlarge': { vcpu: 8, memory: '32 GiB' },
  't4g.medium': { vcpu: 2, memory: '4 GiB' },
  't4g.large': { vcpu: 2, memory: '8 GiB' },
  't4g.xlarge': { vcpu: 4, memory: '16 GiB' },
  't4g.2xlarge': { vcpu: 8, memory: '32 GiB' },
  't4g.4xlarge': { vcpu: 16, memory: '64 GiB' },
  'g5.xlarge': { vcpu: 4, memory: '16 GiB' },
  'g5.2xlarge': { vcpu: 8, memory: '32 GiB' },
  'g5.4xlarge': { vcpu: 16, memory: '64 GiB' },
  'g5.8xlarge': { vcpu: 32, memory: '128 GiB' },
  'g5.12xlarge': { vcpu: 48, memory: '192 GiB' }
};

const nodeArchitectureLabel = (node) => (
  node.hardwareProfile === 'nvidia-gpu'
    ? 'AMD64 · NVIDIA GPU'
    : node.architecture ? node.architecture.toUpperCase() : 'Unavailable'
);

const deploymentLabel = (node) => {
  if (node.nodeType === 'ephemeral') return '';
  return (node.deploymentStatus || 'DEPLOYING').replace(/(^|[-_\s])(\w)/g, (_match, prefix, letter) => `${prefix}${letter.toUpperCase()}`);
};

const Dashboard = () => {
  const { user, signOut, getIdToken } = useAuth();
  const [backendDeploymentAt, setBackendDeploymentAt] = useState(config.backendDeploymentAt || '');
  // Nodes are server state.  Do not restore them from localStorage: a deleted
  // node otherwise survives a page reload and can appear to have live services.
  const [nodes, setNodes] = useState([]);
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const nodeRefreshGate = useRef(createRefreshGate());
  const removedNodeIds = useRef(new Set());
  const [selectedNodeId, setSelectedNodeId] = useState('');
  const [selectedNodeGroup, setSelectedNodeGroup] = useState('');
  const [collapsedNodeGroups, setCollapsedNodeGroups] = useState({ lab: false, cloud: false });
  const [launching, setLaunching] = useState('');
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [showInternalModules, setShowInternalModules] = useState(false);
  const [refreshingNodes, setRefreshingNodes] = useState(false);
  const [nodeRefreshMessage, setNodeRefreshMessage] = useState('');
  const nodeRefreshHadError = useRef(false);
  const [monitorStreamState, setMonitorStreamState] = useState('not-applicable');
  const [serviceRouteStates, setServiceRouteStates] = useState({});
  const serviceProbeSuccesses = useRef(new Map());
  const serviceProbesInFlight = useRef(new Set());
  const [showCreateNode, setShowCreateNode] = useState(false);
  const [moduleEditNode, setModuleEditNode] = useState(null);
  const [nodeMode, setNodeMode] = useState('cloud');
  const [nodeName, setNodeName] = useState('');
  const [cloudHardwareProfile, setCloudHardwareProfile] = useState('standard-amd64');
  const [cloudArchitecture, setCloudArchitecture] = useState('amd64');
  const [cloudInstanceType, setCloudInstanceType] = useState('t3a.medium');
  const cloudInstanceTypesByProfile = {
    'standard-amd64': {
      't3a.medium': '2 vCPU · 4 GiB memory',
      't3a.large': '2 vCPU · 8 GiB memory',
      't3a.xlarge': '4 vCPU · 16 GiB memory',
      't3a.2xlarge': '8 vCPU · 32 GiB memory'
    },
    'standard-arm64': {
      't4g.medium': '2 vCPU · 4 GiB memory',
      't4g.large': '2 vCPU · 8 GiB memory',
      't4g.xlarge': '4 vCPU · 16 GiB memory',
      't4g.2xlarge': '8 vCPU · 32 GiB memory',
      't4g.4xlarge': '16 vCPU · 64 GiB memory'
    },
    'nvidia-gpu': {
      'g5.xlarge': '4 vCPU · 16 GiB memory · NVIDIA A10G GPU (24 GiB VRAM)',
      'g5.2xlarge': '8 vCPU · 32 GiB memory · NVIDIA A10G GPU (24 GiB VRAM)',
      'g5.4xlarge': '16 vCPU · 64 GiB memory · NVIDIA A10G GPU (24 GiB VRAM)',
      'g5.8xlarge': '32 vCPU · 128 GiB memory · NVIDIA A10G GPU (24 GiB VRAM)',
      'g5.12xlarge': '48 vCPU · 192 GiB memory · NVIDIA A10G GPU (24 GiB VRAM)'
    }
  };
  const cloudInstanceTypes = cloudInstanceTypesByProfile[cloudHardwareProfile];
  const [nodeDisplayName, setNodeDisplayName] = useState('');
  const [nodeDisplayNameEdited, setNodeDisplayNameEdited] = useState(false);
  const [nodeLocation, setNodeLocation] = useState('');
  const [manifestCatalog, setManifestCatalog] = useState(EMPTY_MANIFEST_CATALOG);
  const [manifestCatalogError, setManifestCatalogError] = useState('');
  const [manifestPreset, setManifestPreset] = useState('base');
  const [nodeTimeout, setNodeTimeout] = useState('4h');
  const [createNodeDialog, setCreateNodeDialog] = useState(null);
  const [manifestChosenInDialog, setManifestChosenInDialog] = useState(false);
  const [manifestModules, setManifestModules] = useState([]);
  const [showCustomModulePicker, setShowCustomModulePicker] = useState(false);
  const [customManifestModules, setCustomManifestModules] = useState([]);
  const [modulePickerTags, setModulePickerTags] = useState([]);
  const [loadingModulePickerTags, setLoadingModulePickerTags] = useState(false);
  const [modulePickerTagsError, setModulePickerTagsError] = useState('');
  const [nodeModuleOverrides, setNodeModuleOverrides] = useState({});
  const [nodeModuleOptionDraft, setNodeModuleOptionDraft] = useState(null);
  const [nodePortError, setNodePortError] = useState('');
  const [tagSettingsNode, setTagSettingsNode] = useState(null);
  const [settingsTab, setSettingsTab] = useState('image-tags');
  const [catalogFilter, setCatalogFilter] = useState('');
  const [presetFilter, setPresetFilter] = useState('');
  const [catalogEditorExpanded, setCatalogEditorExpanded] = useState(false);
  const [catalogDraft, setCatalogDraft] = useState(null);
  const [catalogOptionDraft, setCatalogOptionDraft] = useState(null);
  const [catalogRuntimeOptionsOpen, setCatalogRuntimeOptionsOpen] = useState(false);
  const [catalogSaving, setCatalogSaving] = useState(false);
  const [catalogImagesByModule, setCatalogImagesByModule] = useState({});
  const [catalogImagesErrors, setCatalogImagesErrors] = useState({});
  const [catalogImageOrder, setCatalogImageOrder] = useState('newest');
  const [catalogRefreshing, setCatalogRefreshing] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [presetDraft, setPresetDraft] = useState(null);
  const [presetModuleSelection, setPresetModuleSelection] = useState(null);
  const [presetModuleFilter, setPresetModuleFilter] = useState('');
  const [presetModuleNotice, setPresetModuleNotice] = useState('');
  const [presetSaving, setPresetSaving] = useState(false);
  const [contractBusy, setContractBusy] = useState(false);
  const [presetError, setPresetError] = useState('');
  const [networkSettingsAccess, setNetworkSettingsAccess] = useState('checking');
  const [tagSettings, setTagSettings] = useState([]);
  const [moduleSettings, setModuleSettings] = useState({});
  const [effectiveModuleSettings, setEffectiveModuleSettings] = useState({});
  const [moduleSecrets, setModuleSecrets] = useState({});
  const [moduleSettingDefinitions, setModuleSettingDefinitions] = useState([]);
  const [expandedModuleSettings, setExpandedModuleSettings] = useState({});
  const [loadingTagSettings, setLoadingTagSettings] = useState(false);
  const isNodeSettingsDialog = tagSettingsNode?.id && tagSettingsNode.id !== 'default-image-tags';
  const runningNodeModules = useMemo(() => (
    isNodeSettingsDialog && tagSettingsNode.runtimeStatusAvailable === true
      ? (tagSettingsNode.runtimeModules || []).filter((moduleItem) => moduleItem.status === 'running')
      : []
  ), [isNodeSettingsDialog, tagSettingsNode]);
  const runningNodeModuleIds = useMemo(() => new Set(runningNodeModules.map((moduleItem) => moduleItem.id)), [runningNodeModules]);
  const visibleTagSettings = useMemo(() => (
    isNodeSettingsDialog ? tagSettings.filter((moduleItem) => runningNodeModuleIds.has(moduleItem.id)) : tagSettings
  ), [isNodeSettingsDialog, runningNodeModuleIds, tagSettings]);
  const visibleModuleSettingDefinitions = useMemo(() => {
    if (!isNodeSettingsDialog) return moduleSettingDefinitions;
    const catalogById = new Map((manifestCatalog.modules || []).map((moduleItem) => [moduleItem.id, moduleItem]));
    const definitionsById = new Map(moduleSettingDefinitions.map((moduleItem) => [moduleItem.id, moduleItem]));
    return runningNodeModules.map((runtimeModule) => {
      const catalogModule = catalogById.get(runtimeModule.id);
      const definitionModule = definitionsById.get(runtimeModule.id);
      return {
        id: runtimeModule.id,
        name: definitionModule?.name || catalogModule?.name || runtimeModule.id,
        settings: definitionModule?.settings || []
      };
    });
  }, [isNodeSettingsDialog, manifestCatalog.modules, moduleSettingDefinitions, runningNodeModules]);
  const [savingTagSettings, setSavingTagSettings] = useState(false);
  const [tagSettingsError, setTagSettingsError] = useState('');
  const [managedUsers, setManagedUsers] = useState([]);
  const [managedUserGroups, setManagedUserGroups] = useState([]);
  const [selectedManagedUsernames, setSelectedManagedUsernames] = useState([]);
  const [managedUserSearch, setManagedUserSearch] = useState('');
  const [loadingManagedUsers, setLoadingManagedUsers] = useState(false);
  const [managingUsername, setManagingUsername] = useState('');
  const [userManagementError, setUserManagementError] = useState('');
  const [allowAnyEndavaEmail, setAllowAnyEndavaEmail] = useState(false);
  const [loadingRegistrationPolicy, setLoadingRegistrationPolicy] = useState(false);
  const [savingRegistrationPolicy, setSavingRegistrationPolicy] = useState(false);
  const [registrationPolicyError, setRegistrationPolicyError] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingUserInvite, setSendingUserInvite] = useState(false);
  const [userInviteMessage, setUserInviteMessage] = useState('');
  const [miniTopNode, setMiniTopNode] = useState(null);
  const [miniTopSnapshot, setMiniTopSnapshot] = useState(null);
  const [miniTopError, setMiniTopError] = useState('');
  const [miniTopDetail, setMiniTopDetail] = useState('cpu');
  const [headerMiniTopSnapshot, setHeaderMiniTopSnapshot] = useState(null);
  const [accessMode, setAccessMode] = useState('private');
  const [publicServices, setPublicServices] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [showInvitePicker, setShowInvitePicker] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [creatingNode, setCreatingNode] = useState(false);
  const [updatingModules, setUpdatingModules] = useState(false);
  const [createNodeError, setCreateNodeError] = useState('');
  const [moduleUpdateError, setModuleUpdateError] = useState('');
  const [deletingNodeId, setDeletingNodeId] = useState('');
  const [initialNodeLoadComplete, setInitialNodeLoadComplete] = useState(false);
  const [initialSettingsCheckComplete, setInitialSettingsCheckComplete] = useState(false);
  const didInitialNodeRefresh = useRef(false);
  const defaultTagSettingsCache = useRef(null);
  const defaultTagSettingsRequest = useRef(null);
  const defaultTagSettingsDirty = useRef(false);
  const moduleConfigCache = useRef(new Map());
  const moduleConfigRequests = useRef(new Map());
  const managedUsersCache = useRef(null);
  const managedUsersRequest = useRef(null);

  useEffect(() => {
    // Remove node lists written by versions that persisted this server state.
    window.localStorage.removeItem('davax-demo-nodes');
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadBuildInfo = async () => {
      try {
        const idToken = await getIdToken();
        if (!idToken) return;
        const response = await fetch(CONTROL_API.buildInfo, { headers: { Authorization: idToken } });
        if (!response.ok) return;
        const payload = await response.json();
        if (!cancelled && payload.backendDeploymentAt) setBackendDeploymentAt(payload.backendDeploymentAt);
      } catch (_error) {
        // Keep the build-time value if live backend metadata is unavailable.
      }
    };
    loadBuildInfo();
    return () => { cancelled = true; };
  }, [getIdToken]);

  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) || null,
    [nodes, selectedNodeId]
  );
  const selectedDeviceId = selectedNode?.deviceId || selectedNode?.sessionId || selectedNodeId;

  const selectedNodeHasMonitor = nodeHasModuleMonitor(selectedNode);
  const selectedNodeIsOnline = selectedNode?.status === 'online';

  useEffect(() => {
    // Module status is meaningful only after the node is confirmed online.
    // In particular, do not request an SSE token while a newly launched node
    // is still provisioning: its FRP route and module-monitor may not exist.
    if (!selectedNodeId || !selectedNodeHasMonitor || !selectedNodeIsOnline || typeof window.EventSource !== 'function') {
      setMonitorStreamState('not-applicable');
      return undefined;
    }

    setMonitorStreamState('waiting');

    let cancelled = false;
    let source;
    let retryTimer;
    let renewalTimer;

    const applyCloudEvent = (message) => {
      try {
        const cloudEvent = JSON.parse(message.data);
        if (cancelled || !monitorEventMatches({ deviceId: selectedDeviceId }, cloudEvent?.data)) return;
        let updates = [];
        let replaceRuntimeModules = false;
        if (cloudEvent.type === 'davax.module.status.snapshot.v1') {
          updates = (cloudEvent.data?.modules || []).map(monitorRuntimeModule);
          replaceRuntimeModules = true;
          // A live stream proves the monitor itself is running, even though it
          // deliberately omits itself from the observed module snapshot.
          if (!updates.some((moduleItem) => moduleItem.id === 'module-monitor')) {
            updates.push({ id: 'module-monitor', status: 'running', description: '' });
          }
        } else if (cloudEvent.type === 'davax.module.status.changed.v1' && cloudEvent.data?.moduleId) {
          updates = [monitorRuntimeModule({
            moduleId: cloudEvent.data.moduleId,
            ...(cloudEvent.data.current || {})
          })];
        }
        if (!updates.length) return;
        setNodes((currentNodes) => currentNodes.map((node) => (
          node.id === selectedNodeId
            ? {
              ...node,
              runtimeModules: replaceRuntimeModules
                ? updates
                : mergeMonitorRuntimeModules(node.runtimeModules, updates),
              runtimeStatusAvailable: true,
              runtimeStatusSource: 'live'
            }
            : node
        )));
      } catch {
        // Ignore malformed events and retain the API polling fallback.
      }
    };

    const scheduleRetry = () => {
      window.clearTimeout(retryTimer);
      retryTimer = window.setTimeout(() => void connect(), 5000);
    };

    const connect = async () => {
      if (cancelled) return;
      setMonitorStreamState((current) => current === 'live' ? current : 'connecting');
      try {
        const idToken = await getIdToken();
        if (!idToken) throw new Error('The login session has expired');
        const response = await fetch(
          `${config.aws.apiEndpoint}/demo-token?nodeId=${encodeURIComponent(selectedNodeId)}&service=module-monitor`,
          { headers: { Authorization: idToken } }
        );
        if (!response.ok) throw new Error(`Monitor access returned ${response.status}`);
        const access = await response.json();
        if (cancelled) return;
        if (!access.demoUrl) throw new Error('Monitor access did not return a URL');
        const streamUrl = new URL(access.demoUrl);
        streamUrl.pathname = '/events';
        source?.close();
        const eventSource = new window.EventSource(streamUrl.toString());
        source = eventSource;
        eventSource.addEventListener('davax.module.status.snapshot.v1', applyCloudEvent);
        eventSource.addEventListener('davax.module.status.changed.v1', applyCloudEvent);
        eventSource.onopen = () => {
          if (source !== eventSource) return;
          window.clearTimeout(retryTimer);
          if (!cancelled) setMonitorStreamState('live');
        };
        eventSource.onerror = () => {
          if (cancelled || source !== eventSource) return;
          eventSource.close();
          source = undefined;
          window.clearTimeout(renewalTimer);
          setMonitorStreamState('waiting');
          scheduleRetry();
        };

        const renewAfterMs = Math.max(30000, ((access.expiresInSeconds || 900) - 30) * 1000);
        window.clearTimeout(renewalTimer);
        renewalTimer = window.setTimeout(() => {
          if (source === eventSource) {
            eventSource.close();
            source = undefined;
          }
          void connect();
        }, renewAfterMs);
      } catch {
        if (cancelled) return;
        setMonitorStreamState('waiting');
        scheduleRetry();
      }
    };

    void connect();
    return () => {
      cancelled = true;
      source?.close();
      window.clearTimeout(retryTimer);
      window.clearTimeout(renewalTimer);
    };
  }, [getIdToken, selectedNodeHasMonitor, selectedNodeId, selectedNodeIsOnline, selectedDeviceId]);

  const filteredManagedUsers = useMemo(() => {
    const query = managedUserSearch.trim().toLowerCase();
    if (!query) return managedUsers;
    return managedUsers.filter((managedUser) => (
      managedUser.username.toLowerCase().includes(query) || managedUser.email.toLowerCase().includes(query)
    ));
  }, [managedUsers, managedUserSearch]);

  const nodeGroups = useMemo(() => ({
    lab: nodes.filter((node) => node.nodeType !== 'ephemeral'),
    cloud: nodes.filter((node) => node.nodeType === 'ephemeral')
  }), [nodes]);

  const visibleServices = useMemo(
    () => {
      if (!selectedNode || selectedNode.servicesReady === false) {
        return [];
      }
      const allowedServices = selectedNode.allowedServices;
      const catalogServices = manifestCatalog.modules
        .map((moduleItem) => ({
          ...moduleItem,
          browserOpenable: selectedNode.moduleRuntimeOptions?.[moduleItem.id]?.browserUi ?? moduleItem.browserOpenable,
          port: selectedNode.moduleRuntimeOptions?.[moduleItem.id]?.hostPort ?? selectedNode.moduleRuntimeOptions?.[moduleItem.id]?.exposedPort ?? moduleItem.hostPort ?? moduleItem.port
        }))
        .filter((moduleItem) => moduleItem.browserOpenable === true && Number(moduleItem.port) > 0)
        .filter((moduleItem) => !SERVICES.some((service) => sameService(service.serviceKey, moduleItem.serviceKey) || service.id === moduleItem.id))
        .map((moduleItem) => ({ id: moduleItem.id, serviceKey: moduleItem.serviceKey || moduleItem.id, name: moduleItem.name, description: moduleItem.description, port: String(moduleItem.port), session: (nodeId) => `${nodeId}-${moduleItem.serviceKey || moduleItem.id}`, available: true }));
      return [...SERVICES, ...catalogServices].filter((service) => (
        service.available
        && isUserLaunchableServiceKey(service.serviceKey)
        && (!allowedServices || allowedServices.some((allowedService) => sameService(allowedService, service.serviceKey)))
      ));
    },
    [manifestCatalog, selectedNode]
  );

  const customSelectableModules = useMemo(
    () => [...manifestCatalog.modules]
      .filter((moduleItem) => !REQUIRED_BASE_MODULE_IDS.includes(moduleItem.id))
      .sort((left, right) => (left.name || left.id).localeCompare(right.name || right.id, undefined, { sensitivity: 'base' })),
    [manifestCatalog]
  );

  const selectedManifestModules = useMemo(
    () => manifestModuleSummary({
      catalog: manifestCatalog,
      preset: manifestPreset,
      modules: manifestModules
    }),
    [manifestCatalog, manifestModules, manifestPreset]
  );

  const publicServiceOptions = useMemo(() => {
    const byKey = new Map();
    selectedManifestModules.forEach((moduleItem) => {
      const override = nodeModuleOverrides[moduleItem.id] || {};
      const browserOpenable = override.browserUi ?? moduleItem.browserOpenable;
      const port = override.hostPort ?? override.exposedPort ?? moduleItem.hostPort ?? moduleItem.port;
      const serviceKey = moduleItem.serviceKey || moduleItem.id;
      if (browserOpenable === true && Number(port) > 0 && isShareableServiceKey(serviceKey) && !byKey.has(serviceKey)) {
        byKey.set(serviceKey, { serviceKey, name: moduleItem.name || serviceKey });
      }
    });
    return [...byKey.values()];
  }, [nodeModuleOverrides, selectedManifestModules]);

  const deployedModules = useMemo(
    () => {
      if (!selectedNode) {
        return [];
      }
      const moduleMap = new Map(manifestCatalog.modules.map((moduleItem) => [moduleItem.id, moduleItem]));
      const modules = Array.isArray(selectedNode.deployedModules) && selectedNode.deployedModules.length
        ? selectedNode.deployedModules
        : deriveModuleInventory({
          catalog: manifestCatalog,
          preset: selectedNode.manifestPreset || 'base',
          modules: selectedNode.manifestModules || []
        });
      const expectedModules = modules
        .filter((moduleItem) => !['edgeAgent', 'edgeHub', '$edgeAgent', '$edgeHub'].includes(moduleItem.id))
        .map((moduleItem) => {
          const catalogItem = moduleMap.get(moduleItem.id) || {};
          return {
            ...catalogItem,
            ...moduleItem,
            name: moduleItem.name || catalogItem.name || moduleItem.id,
            description: moduleItem.description || catalogItem.description || '',
            browserOpenable: selectedNode.moduleRuntimeOptions?.[moduleItem.id]?.browserUi ?? (moduleItem.browserOpenable === true || catalogItem.browserOpenable === true),
            serviceKey: moduleItem.serviceKey || catalogItem.serviceKey || '',
            port: selectedNode.moduleRuntimeOptions?.[moduleItem.id]?.hostPort ?? selectedNode.moduleRuntimeOptions?.[moduleItem.id]?.exposedPort ?? moduleItem.hostPort ?? moduleItem.port ?? catalogItem.hostPort ?? catalogItem.port ?? ''
          };
        });
      const expectedIds = new Set(expectedModules.map((moduleItem) => moduleItem.id));
      const unexpectedRuntimeModules = (selectedNode.runtimeModules || [])
        .filter((moduleItem) => !expectedIds.has(moduleItem.id))
        .map((moduleItem) => ({
          id: moduleItem.id,
          name: moduleItem.id,
          description: 'Reported by the device but not in the selected manifest.',
          browserOpenable: false,
          unexpectedRuntime: true
        }));
      return [...expectedModules, ...unexpectedRuntimeModules];
    },
    [manifestCatalog, selectedNode]
  );

  const visibleDeployedModules = useMemo(
    () => showInternalModules
      ? deployedModules
      : deployedModules.filter((moduleItem) => (
        (moduleItem.browserOpenable === true && moduleItem.id !== 'module-monitor')
        || moduleItem.unexpectedRuntime === true
      )),
    [deployedModules, showInternalModules]
  );

  const serviceProbeCandidates = useMemo(() => visibleServices.map((service) => {
    const moduleItem = deployedModules.find((candidate) => (
      candidate.browserOpenable === true && sameService(candidate.serviceKey, service.serviceKey)
    ));
    const runtime = moduleItem && (selectedNode?.runtimeModules || []).find((item) => item.id === moduleItem.id);
    return moduleItem ? {
      service,
      moduleId: moduleItem.id,
      locallyRunning: String(runtime?.status || '').toLowerCase() === 'running'
    } : null;
  }).filter(Boolean), [deployedModules, selectedNode, visibleServices]);

  useEffect(() => {
    serviceProbeSuccesses.current.clear();
    serviceProbesInFlight.current.clear();
    setServiceRouteStates({});
  }, [selectedNodeId]);

  useEffect(() => {
    if (!selectedNode || !serviceProbeCandidates.length) return undefined;
    let cancelled = false;

    setServiceRouteStates((current) => Object.fromEntries(serviceProbeCandidates.map((candidate) => [
      candidate.service.serviceKey,
      candidate.locallyRunning
        ? (current[candidate.service.serviceKey] || 'VALIDATING')
        : 'LOCAL_NOT_READY'
    ])));
    for (const candidate of serviceProbeCandidates) {
      if (!candidate.locallyRunning) serviceProbeSuccesses.current.delete(candidate.service.serviceKey);
    }

    const probeCandidate = async (candidate) => {
      const serviceKey = candidate.service.serviceKey;
      const priorSuccess = serviceProbeSuccesses.current.get(serviceKey);
      if (
        !candidate.locallyRunning
        || serviceProbesInFlight.current.has(serviceKey)
        || (priorSuccess?.count || 0) >= 2
        || (priorSuccess?.count === 1 && Date.now() - priorSuccess.at < 1900)
      ) return;
      serviceProbesInFlight.current.add(serviceKey);
      try {
        const idToken = await getIdToken();
        if (!idToken) throw new Error('The login session has expired');
        const response = await fetch(
          `${config.aws.apiEndpoint}/demo-token?nodeId=${encodeURIComponent(selectedNode.id)}&service=${encodeURIComponent(serviceKey)}&probe=true`,
          { headers: { Authorization: idToken } }
        );
        if (!response.ok) throw new Error(`Route probe returned ${response.status}`);
        const result = await response.json();
        if (cancelled) return;
        if (result.routeStatus === 'NAVIGABLE') {
          const successes = (serviceProbeSuccesses.current.get(serviceKey)?.count || 0) + 1;
          serviceProbeSuccesses.current.set(serviceKey, { count: successes, at: Date.now() });
          setServiceRouteStates((current) => ({
            ...current,
            [serviceKey]: successes >= 2 ? 'NAVIGABLE' : 'VALIDATING'
          }));
        } else {
          serviceProbeSuccesses.current.delete(serviceKey);
          setServiceRouteStates((current) => ({
            ...current,
            [serviceKey]: result.routeStatus || 'UNREACHABLE'
          }));
        }
      } catch {
        if (!cancelled) {
          serviceProbeSuccesses.current.delete(serviceKey);
          setServiceRouteStates((current) => ({ ...current, [serviceKey]: 'UNREACHABLE' }));
        }
      } finally {
        serviceProbesInFlight.current.delete(serviceKey);
      }
    };

    const probeRunningServices = () => {
      for (const candidate of serviceProbeCandidates) void probeCandidate(candidate);
    };
    probeRunningServices();
    const interval = window.setInterval(probeRunningServices, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [getIdToken, selectedNode, serviceProbeCandidates]);

  const loadMiniTopSnapshot = useCallback(async () => {
    if (!miniTopNode) {
      return;
    }
    // Keep the last sample and any error visible while the background request runs.
    try {
      const idToken = await getIdToken();
      if (!idToken) {
        throw new Error('Your login session has expired. Sign in again to continue.');
      }
      const miniTopService = SERVICES.find((service) => service.id === 'mini-top');
      const response = await fetch(
        `${config.aws.apiEndpoint}/demo-token?session=${encodeURIComponent(miniTopService.session(miniTopNode.id))}`,
        { headers: { Authorization: idToken } }
      );
      if (!response.ok) {
        const details = await response.text();
        throw new Error(`Access request failed (${response.status}). ${details}`);
      }
      const result = await response.json();
      if (!result.demoUrl) {
        throw new Error('The access service did not return a Mini Top URL.');
      }
      const snapshotUrl = new URL(result.demoUrl);
      snapshotUrl.pathname = '/snapshot';
      const snapshotResponse = await fetch(snapshotUrl.toString(), {
        credentials: 'include'
      });
      if (!snapshotResponse.ok) {
        const errorText = await snapshotResponse.text();
        let errorMessage = errorText;
        try {
          errorMessage = JSON.parse(errorText).message || errorText;
        } catch (parseError) {
          // CloudFront token errors are plain text rather than JSON.
        }
        throw new Error(errorMessage || `Mini Top returned ${snapshotResponse.status}.`);
      }
      const snapshot = await snapshotResponse.json();
      setMiniTopSnapshot(snapshot);
      setMiniTopError('');
    } catch (snapshotError) {
      setMiniTopError(snapshotError.message || 'Could not load Mini Top metrics.');
    }
  }, [miniTopNode, getIdToken]);

  useEffect(() => {
    if (!miniTopNode) {
      return undefined;
    }
    loadMiniTopSnapshot();
    const interval = window.setInterval(loadMiniTopSnapshot, 10000);
    return () => window.clearInterval(interval);
  }, [miniTopNode, loadMiniTopSnapshot]);

  const selectedNodeHasMiniTop = Boolean(selectedNode?.deployedModules?.some((moduleItem) => (
    (typeof moduleItem === 'string' ? moduleItem : moduleItem.id) === 'mini-top'
  )));

  const loadHeaderMiniTopSnapshot = useCallback(async () => {
    if (!selectedNode || !selectedNodeHasMiniTop) {
      setHeaderMiniTopSnapshot(null);
      return;
    }
    try {
      const idToken = await getIdToken();
      if (!idToken) return;
      const response = await fetch(
        `${config.aws.apiEndpoint}/demo-token?session=${encodeURIComponent(`${selectedNode.id}-mini-top`)}&nodeId=${encodeURIComponent(selectedNode.id)}&service=mini-top`,
        { headers: { Authorization: idToken } }
      );
      if (!response.ok) return;
      const result = await response.json();
      if (!result.demoUrl) return;
      const snapshotUrl = new URL(result.demoUrl);
      snapshotUrl.pathname = '/snapshot';
      const snapshotResponse = await fetch(snapshotUrl.toString(), { credentials: 'include' });
      if (snapshotResponse.ok) setHeaderMiniTopSnapshot(await snapshotResponse.json());
    } catch {
      // The header remains compact and non-blocking when Mini Top is starting.
    }
  }, [getIdToken, selectedNode, selectedNodeHasMiniTop]);

  useEffect(() => {
    loadHeaderMiniTopSnapshot();
    if (!selectedNodeHasMiniTop) return undefined;
    const interval = window.setInterval(loadHeaderMiniTopSnapshot, 10000);
    return () => window.clearInterval(interval);
  }, [loadHeaderMiniTopSnapshot, selectedNodeHasMiniTop]);

  const openMiniTopModal = () => {
    if (!selectedNode) {
      return;
    }
    setMiniTopSnapshot(null);
    setMiniTopError('');
    setMiniTopDetail('cpu');
    setMiniTopNode(selectedNode);
  };

  const refreshNodes = useCallback(async ({ foreground = false } = {}) => {
    const gate = nodeRefreshGate.current;
    const revision = gate.begin();
    if (revision === null) return;
    const previousNodes = nodesRef.current;
    const removed = new Set(removedNodeIds.current);
    // Scheduled polling stays quiet; explicit refreshes retain their progress indicator.
    if (foreground) setRefreshingNodes(true);

    try {
      const idToken = await getIdToken();
      if (!idToken) {
        throw new Error('Sign in again to refresh managed nodes.');
      }

      const registryResponse = await fetch(
        `${config.aws.apiEndpoint}/demo-nodes`,
        { headers: { Authorization: idToken } }
      );
      if (!registryResponse.ok) {
        throw new Error(`Node registry returned ${registryResponse.status}`);
      }
      const registryResult = await registryResponse.json();
      let projectsBySession = new Map();
      try {
        const projectsResponse = await fetch(`${config.aws.apiEndpoint}/projects`, {
          headers: { Authorization: idToken }
        });
        if (projectsResponse.ok) {
          const projectsResult = await projectsResponse.json();
          projectsBySession = new Map((projectsResult.projects || [])
            .filter((project) => project.sessionId)
            .map((project) => [project.sessionId, project]));
        }
      } catch {
        // Project ownership is supplementary; node discovery should continue.
      }
      const registryNodes = (registryResult.nodes || []).map((node) => ({
        ...nodeIdentity(node),
        name: node.displayName || node.nodeId,
        description: node.description || 'Registered edge node',
        status: ['PROVISIONING', 'CREATING'].includes(node.status)
          ? 'initializing'
          : node.status === 'ONLINE' ? 'online' : 'offline',
        location: node.location || 'Registered node',
        registryNode: true,
        nodeType: node.nodeType || 'physical',
        accessMode: node.accessMode || 'private',
        publicServices: node.publicServices || [],
        manifestPreset: node.manifestPreset || 'base',
        manifestModules: normalizeList(node.manifestModules),
        imageTags: normalizeImageTags(node.imageTags),
        moduleSettings: node.moduleSettings || {},
        moduleRuntimeOptions: node.moduleRuntimeOptions || {},
        deployedModules: node.deployedModules || [],
        runtimeModules: node.runtimeModules || [],
        runtimeStatusAvailable: node.runtimeStatusAvailable === true,
        deploymentStatus: node.deploymentStatus || 'DEPLOYING',
        fabric: node.fabric || null,
        setup: node.setup || null,
        servicesReady: node.nodeType === 'ephemeral' ? node.status === 'ONLINE' : node.deploymentStatus === 'READY',
        allowedServices: node.services || [],
        sessionId: node.sessionId,
        instanceId: node.instanceId,
        ssmInstanceId: node.ssmInstanceId || node.instanceId,
        architecture: node.architecture || '',
        hardwareProfile: node.hardwareProfile || '',
        instanceType: node.instanceType || '',
        projectOwner: node.nodeType === 'ephemeral' ? (projectsBySession.get(node.sessionId)?.creatorEmail || projectsBySession.get(node.sessionId)?.ownerEmail || node.ownerEmail || '') : ''
      }));

      let refreshFailed = false;
      const sessionNodes = mergeNodeRefresh(previousNodes, registryNodes, removed)
        .filter((node) => node.nodeType === 'ephemeral' && node.sessionId);
      const refreshedSessions = await Promise.all(sessionNodes.map(async (node) => {
        try {
          const response = await fetch(
            CONTROL_API.session(node.sessionId || node.id),
            { headers: { Authorization: idToken } }
          );

          if (response.status === 404) {
            removed.add(node.id);
            return null;
          }

          if (!response.ok) {
            throw new Error(`Status ${response.status}`);
          }

          const session = await response.json();
          if (['TERMINATED', 'FAILED', 'STOPPING'].includes(session.status)) {
            removed.add(node.id);
            return null;
          }

          const initializingStatuses = ['PROVISIONING', 'CREATING'];
          return {
            ...node,
            deviceId: session.deviceId || node.deviceId || node.id,
            status: initializingStatuses.includes(session.status)
              ? 'initializing'
              : session.status === 'READY' ? 'online' : 'offline',
            servicesReady: ['APPLIED', 'DEPLOYED'].includes(session.manifestStatus),
            fabric: session.fabric || node.fabric || null,
            setup: session.setup || node.setup || null,
            manifestPreset: session.manifestPreset || node.manifestPreset || 'base',
            manifestModules: normalizeList(session.manifestModules || node.manifestModules),
            imageTags: normalizeImageTags(session.imageTags || node.imageTags),
            moduleSettings: session.moduleSettings || node.moduleSettings || {},
            moduleRuntimeOptions: session.moduleRuntimeOptions || node.moduleRuntimeOptions || {},
            publicServices: session.publicServices || node.publicServices || [],
            deployedModules: session.deployedModules || node.deployedModules || [],
            runtimeModules: session.runtimeModules || [],
            runtimeStatusAvailable: session.runtimeStatusAvailable === true,
            allowedServices: session.allowedServices
              ? session.allowedServices.split(',').map((service) => service.trim()).filter(Boolean)
              : node.allowedServices,
            instanceId: session.instanceId || node.instanceId,
            ssmInstanceId: session.ssmInstanceId || session.instanceId || node.ssmInstanceId,
            expiresAt: session.expiresAt || node.expiresAt,
            architecture: session.architecture || node.architecture,
            hardwareProfile: session.hardwareProfile || node.hardwareProfile,
            instanceType: session.instanceType || node.instanceType,
            projectOwner: projectsBySession.get(session.sessionId || node.sessionId)?.creatorEmail || projectsBySession.get(session.sessionId || node.sessionId)?.ownerEmail || node.projectOwner || node.ownerEmail || ''
          };
        } catch (refreshError) {
          refreshFailed = true;
          return { ...node, status: 'unknown', runtimeModules: [], runtimeStatusAvailable: false };
        }
      }));

      if (!gate.isCurrent(revision)) return;
      const availableNodes = mergeNodeRefresh(registryNodes, refreshedSessions, removed);
      const removedCount = previousNodes.filter(
        (node) => !availableNodes.some((available) => available.id === node.id)
      ).length;

      setNodes((currentNodes) => gate.isCurrent(revision) ? availableNodes.map((availableNode) => {
        const currentNode = currentNodes.find((node) => node.id === availableNode.id);
        return {
          ...availableNode,
          ...pollingRuntime(availableNode, currentNode)
        };
      }) : currentNodes);
      setSelectedNodeId((currentNodeId) => (
        availableNodes.some((node) => node.id === currentNodeId)
          ? currentNodeId
          : ''
      ));
      // Announce meaningful changes and recovery, rather than every successful poll.
      if (removedCount > 0 || refreshFailed || foreground || nodeRefreshHadError.current) {
        setNodeRefreshMessage(
          removedCount > 0
            ? `${removedCount} unavailable ${removedCount === 1 ? 'node' : 'nodes'} removed`
            : refreshFailed
              ? 'Some nodes could not be checked'
              : foreground ? 'Checked just now' : ''
        );
      }
      nodeRefreshHadError.current = refreshFailed;
    } catch (refreshError) {
      if (gate.isCurrent(revision)) {
        nodeRefreshHadError.current = true;
        setNodeRefreshMessage(refreshError.message || 'Could not refresh nodes');
      }
    } finally {
      gate.finish();
      if (foreground) setRefreshingNodes(false);
    }
  }, [getIdToken]);

  useEffect(() => {
    if (didInitialNodeRefresh.current) {
      return;
    }

    didInitialNodeRefresh.current = true;
    refreshNodes().finally(() => setInitialNodeLoadComplete(true));
  }, [refreshNodes]);

  useEffect(() => {
    const gate = nodeRefreshGate.current;
    const interval = window.setInterval(refreshNodes, 5000);
    return () => {
      window.clearInterval(interval);
      gate.invalidate();
    };
  }, [refreshNodes]);

  const resetCreateNodeDraft = () => {
    setNodeName('');
    setNodeDisplayName('');
    setNodeDisplayNameEdited(false);
    setNodeLocation('Local Lab');
    setManifestPreset('base');
    setNodeTimeout('4h');
    setCreateNodeDialog(null);
    setManifestModules([]);
    setCustomManifestModules([]);
    setNodeModuleOverrides({});
    setModulePickerTags([]);
    setModulePickerTagsError('');
    setShowCustomModulePicker(false);
    setNodeModuleOptionDraft(null);
    setAccessMode('private');
    setPublicServices([]);
    setInvitedUsers([]);
    setInviteError('');
    setCreateNodeError('');
  };

  const cancelCreateNode = () => {
    if (creatingNode) return;
    resetCreateNodeDraft();
    setShowCreateNode(false);
  };

  const openCreateNode = () => {
    resetCreateNodeDraft();
    setNodeMode('cloud');
    setNodeName(`cloud-node-${nodes.length + 1}`);
    setCloudHardwareProfile('standard-amd64');
    setCloudArchitecture('amd64');
    setCloudInstanceType('t3a.medium');
    setShowCreateNode(true);
    loadManifestCatalog();
  };

  const loadManifestCatalog = useCallback(async () => {
    setManifestCatalogError('');
    try {
      const idToken = await getIdToken();
      if (!idToken) {
        setManifestCatalogError('Module catalog unavailable. Sign in again and retry.');
        return;
      }
      const response = await fetch(CONTROL_API.catalog, {
        headers: { Authorization: idToken }
      });
      if (!response.ok) {
        setManifestCatalogError('Module catalog unavailable. Retry in a moment.');
        return;
      }
      const result = await response.json();
      if (Array.isArray(result.presets) && Array.isArray(result.modules)) {
        setManifestCatalog(result);
        setNodeTimeout(result.presets.find((preset) => preset.id === 'base')?.sessionTimeout || '4h');
        const imageModules = result.modules.filter(catalogModuleUsesAcr);
        const imageResults = await Promise.all(imageModules.map(async (moduleItem) => {
          try {
            const imageResponse = await fetch(CONTROL_API.catalogModuleImages(moduleItem.id), {
              headers: { Authorization: idToken }
            });
            if (!imageResponse.ok) throw new Error(`Image lookup failed (${imageResponse.status}).`);
            const images = await imageResponse.json();
            if (!Array.isArray(images.digests)) throw new Error('Image lookup returned an invalid response.');
            return { moduleId: moduleItem.id, images };
          } catch (imageError) {
            return { moduleId: moduleItem.id, error: imageError.message || 'Could not load image tags.' };
          }
        }));
        setCatalogImagesByModule((current) => ({
          ...current,
          ...Object.fromEntries(imageResults.filter((item) => item.images).map((item) => [item.moduleId, item.images]))
        }));
        setCatalogImagesErrors(Object.fromEntries(imageResults.filter((item) => item.error).map((item) => [item.moduleId, item.error])));
      } else {
        setManifestCatalogError('Module catalog unavailable. Retry in a moment.');
      }
    } catch {
      setManifestCatalogError('Module catalog unavailable. Retry in a moment.');
    }
  }, [getIdToken]);

  useEffect(() => {
    void loadManifestCatalog();
  }, [loadManifestCatalog]);

  const newCatalogModule = () => ({
    id: '', serviceKey: '', routeIdAuto: true, moduleIdAuto: true, name: '', description: '', imageRepository: '', defaultTag: 'latest', external: false, externalImage: '', hostPort: '', containerPort: '', browserOpenable: false, hostNetwork: true, mounts: [], settings: [], deploymentText: ''
  });

  const normalizeModuleId = (value) => {
    const normalized = String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 63);
    return normalized && !/^[a-z]/.test(normalized) ? `module-${normalized}`.slice(0, 63) : normalized;
  };

  const invalidateCatalogDependentCaches = () => {
    defaultTagSettingsCache.current = null;
    defaultTagSettingsDirty.current = false;
    moduleConfigCache.current.clear();
  };

  const refreshCatalogDependentData = async () => {
    setCatalogRefreshing(true);
    try {
      const result = await fetchModuleConfiguration('', true);
      defaultTagSettingsCache.current = result.modules || [];
      setTagSettings(prepareTagSettings(result.modules || []));
      setModuleSettings(result.moduleSettings || {});
      setEffectiveModuleSettings(result.effectiveModuleSettings || result.moduleSettings || {});
      setModuleSettingDefinitions(result.settings || []);
    } catch (refreshError) {
      setCatalogError(`The catalog was saved, but its settings lists could not be refreshed. ${refreshError.message || ''}`.trim());
    } finally {
      setCatalogRefreshing(false);
    }
  };

  const editCatalogModule = (moduleItem) => {
    setCatalogError('');
    setCatalogImageOrder('newest');
    setCatalogEditorExpanded(true);
    setCatalogDraft({
      ...moduleItem,
      ...modulePortDraft(moduleItem),
      serviceKey: moduleItem.serviceKey || moduleItem.id,
      imageRepository: moduleItem.imageRepository || catalogModuleImageRepository(moduleItem),
      routeIdAuto: false,
      moduleIdAuto: false,
      existing: true,
      defaultTag: catalogModuleTag(moduleItem),
      deploymentText: moduleItem.deployment ? JSON.stringify(moduleItem.deployment, null, 2) : '',
      settings: (moduleItem.settings || []).map((setting) => ({
        ...setting,
        optionsText: (setting.options || []).join(', ')
      }))
    });
  };

  const openNewCatalogModule = () => {
    setCatalogError('');
    setCatalogDraft(newCatalogModule());
    setCatalogEditorExpanded(true);
  };

  const closeCatalogEditor = () => {
    setCatalogDraft(null);
    setCatalogEditorExpanded(false);
  };

  const updateCatalogDraft = (field, value) => {
    setCatalogDraft((current) => {
      if (field === 'serviceKey') {
        const serviceKey = normalizeModuleId(value);
        return {
          ...current,
          serviceKey,
          routeIdAuto: current.existing === true ? false : serviceKey === normalizeModuleId(current.name),
          ...(current.existing !== true && current.moduleIdAuto !== false ? { id: serviceKey } : {})
        };
      }
      if (field === 'name') {
        const serviceKey = normalizeModuleId(value);
        return {
          ...current,
          name: value,
          ...(current.existing !== true && current.routeIdAuto !== false ? {
            serviceKey,
            ...(current.moduleIdAuto !== false ? { id: serviceKey } : {})
          } : {})
        };
      }
      if (field === 'id') {
        const id = normalizeModuleId(value);
        return { ...current, id, moduleIdAuto: current.existing === true ? false : id === normalizeModuleId(current.serviceKey) };
      }
      return { ...current, [field]: value };
    });
  };

  const catalogCreateOptions = () => {
    try {
      const deployment = JSON.parse(catalogDraft?.deploymentText || '{}');
      return JSON.parse(deployment?.settings?.createOptions || '{}');
    } catch {
      return null;
    }
  };

  const updateCatalogCreateOptions = (update) => {
    setCatalogDraft((current) => {
      try {
        const deployment = JSON.parse(current.deploymentText || '{}');
        const createOptions = JSON.parse(deployment?.settings?.createOptions || '{}');
        deployment.settings.createOptions = JSON.stringify(update(createOptions));
        return { ...current, deploymentText: JSON.stringify(deployment, null, 2) };
      } catch {
        return current;
      }
    });
  };

  const newCatalogSetting = () => ({
    id: '', env: '', label: '', description: '', type: 'text', scope: 'shared', required: false, defaultValue: '', optionsText: '', min: '', max: '', maxLength: ''
  });

  const generatedOptionId = (label, usedIds) => {
    const words = String(label || '').trim().match(/[A-Za-z0-9]+/g) || [];
    const stem = words.map((word, index) => (
      index === 0 ? word.toLowerCase() : `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`
    )).join('').replace(/^[^A-Za-z]+/, '');
    const base = (stem || 'option').slice(0, 60);
    let id = base;
    let suffix = 2;
    while (usedIds.has(id)) {
      id = `${base.slice(0, 60)}${suffix}`;
      suffix += 1;
    }
    return id;
  };

  const openCatalogOptionEditor = (index = null) => {
    const setting = index === null ? newCatalogSetting() : catalogDraft.settings[index];
    setCatalogOptionDraft({ index, setting: { ...setting } });
  };

  const updateCatalogOptionDraft = (field, value) => {
    setCatalogOptionDraft((current) => ({ ...current, setting: { ...current.setting, [field]: value } }));
  };

  const saveCatalogOption = (event) => {
    event.preventDefault();
    if (!catalogOptionDraft) return;
    setCatalogDraft((current) => {
      const setting = { ...catalogOptionDraft.setting };
      if (setting.type === 'secret') setting.scope = 'stack';
      if (setting.scope === 'stack') delete setting.defaultValue;
      if (catalogOptionDraft.index === null) {
        setting.id = generatedOptionId(setting.label, new Set(current.settings.map((item) => item.id)));
      }
      return {
        ...current,
        settings: catalogOptionDraft.index === null
          ? [...current.settings, setting]
          : current.settings.map((item, index) => index === catalogOptionDraft.index ? setting : item)
      };
    });
    setCatalogOptionDraft(null);
  };

  const removeCatalogSetting = (index) => {
    setCatalogDraft((current) => ({
      ...current,
      settings: current.settings.filter((_, settingIndex) => settingIndex !== index)
    }));
    setCatalogOptionDraft((current) => current?.index === index ? null : current);
  };

  const saveCatalogModule = async (event) => {
    event.preventDefault();
    if (!catalogDraft) return;
    setCatalogSaving(true);
    setCatalogError('');
    try {
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Sign in again to manage the module catalog.');
      const module = {
        ...catalogDraft,
        settings: catalogDraft.settings.map(({ optionsText, ...setting }) => ({
          ...setting,
          defaultValue: setting.type === 'boolean' && setting.defaultValue !== ''
            ? setting.defaultValue === true || setting.defaultValue === 'true'
            : setting.defaultValue,
          options: setting.type === 'select'
            ? optionsText.split(',').map((option) => option.trim()).filter(Boolean)
            : undefined
        }))
      };
      const { existing, deploymentText, deployment: ignoredDeployment, ...moduleDefinition } = module;
      Object.assign(moduleDefinition, portPayload(catalogDraft, catalogDraft.browserOpenable));
      // Send one authoritative spelling so old aliases cannot conflict.
      delete moduleDefinition.port;
      delete moduleDefinition.exposedPort;
      delete moduleDefinition.uiPort;
      if (moduleDefinition.hostPort === '') delete moduleDefinition.hostPort;
      if (moduleDefinition.containerPort === '') delete moduleDefinition.containerPort;
      delete moduleDefinition.moduleIdAuto;
      delete moduleDefinition.routeIdAuto;
      if (deploymentText.trim()) {
        try {
          moduleDefinition.deployment = JSON.parse(deploymentText);
        } catch {
          throw new Error('Deployment template must contain valid JSON.');
        }
      }
      const catalogModuleUrl = catalogDraft.existing === true
        ? CONTROL_API.catalogModule(moduleDefinition.id)
        : CONTROL_API.catalogModules;
      const response = await fetch(catalogModuleUrl, {
        method: catalogDraft.existing === true ? 'PATCH' : 'POST',
        headers: { Authorization: idToken, 'Content-Type': 'application/json' },
        body: JSON.stringify(moduleDefinition)
      });
      if (!response.ok) throw new Error(`Catalog save failed (${response.status}). ${await response.text()}`);
      const updatedCatalog = await response.json();
      setManifestCatalog(updatedCatalog);
      invalidateCatalogDependentCaches();
      setCatalogDraft(null);
      setCatalogEditorExpanded(false);
      await refreshCatalogDependentData();
      setNodeRefreshMessage(`Saved module definition for ${module.name}. No nodes were changed.`);
    } catch (saveError) {
      setCatalogError(saveError.message || 'Could not save the module definition.');
    } finally {
      setCatalogSaving(false);
    }
  };

  const deleteCatalogModule = async (moduleItem) => {
    if (!window.confirm(`Delete module ${moduleItem.name || moduleItem.id}? This does not change deployed nodes.`)) return;
    setCatalogSaving(true);
    setCatalogError('');
    try {
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Sign in again to manage the module catalog.');
      const response = await fetch(CONTROL_API.catalogModule(moduleItem.id), {
        method: 'DELETE',
        headers: { Authorization: idToken }
      });
      if (!response.ok) throw new Error(`Catalog deletion failed (${response.status}). ${await response.text()}`);
      setManifestCatalog(await response.json());
      invalidateCatalogDependentCaches();
      if (catalogDraft?.id === moduleItem.id) setCatalogDraft(null);
      await refreshCatalogDependentData();
      setNodeRefreshMessage(`Deleted module definition for ${moduleItem.name || moduleItem.id}. No nodes were changed.`);
    } catch (deleteError) {
      setCatalogError(deleteError.message || 'Could not delete the module definition.');
    } finally {
      setCatalogSaving(false);
    }
  };

  const catalogModules = manifestCatalog.modules || [];
  const catalogPresets = (manifestCatalog.presets || []).filter((preset) => preset.id !== 'custom');
  const filteredCatalogModules = filterCatalogItems(catalogModules, catalogFilter)
    .slice().sort((left, right) => (left.name || left.id).localeCompare(right.name || right.id));
  const filteredCatalogPresets = filterCatalogItems(catalogPresets, presetFilter);

  const selectedPresetModules = [...new Set([...REQUIRED_BASE_MODULE_IDS, ...(presetDraft?.modules || [])])]
    .map((id) => catalogModules.find((moduleItem) => moduleItem.id === id) || { id, name: id })
    .sort((left, right) => (left.name || left.id).localeCompare(right.name || right.id));
  const filteredPresetModuleChoices = filterCatalogItems(catalogModules, presetModuleFilter)
    .slice().sort((left, right) => (left.name || left.id).localeCompare(right.name || right.id));

  const newPresetDraft = () => ({ id: '', name: '', description: '', modules: [], defaultInstance: null, existing: false });

  const openPresetEditor = (preset = null) => {
    setPresetError('');
    setPresetModuleNotice('');
    setPresetModuleSelection(null);
    setPresetModuleFilter('');
    setPresetDraft(preset ? { ...preset, defaultInstance: preset.defaultInstance || null, modules: (preset.modules || []).filter((id) => !REQUIRED_BASE_MODULE_IDS.includes(id)), existing: true } : newPresetDraft());
  };

  const updatePresetModules = (modules) => {
    const detachContract = Boolean(presetDraft?.fabricContract) && (!modules.includes('solar-1') || !modules.includes('fabric-agent'));
    setPresetDraft((current) => ({ ...current, modules, ...(detachContract ? { fabricContract: null } : {}) }));
    if (detachContract) setPresetModuleNotice('Fabric contract detached: it requires DataHub backend and Data Fabric Agent.');
  };

  const openPresetModulePicker = () => {
    setPresetModuleSelection([...(presetDraft?.modules || [])]);
    setPresetModuleFilter('');
  };

  const togglePresetModuleChoice = (moduleId) => {
    if (REQUIRED_BASE_MODULE_IDS.includes(moduleId)) return;
    setPresetModuleSelection((current) => current.includes(moduleId)
      ? current.filter((id) => id !== moduleId) : [...current, moduleId]);
  };

  const savePresetModuleSelection = () => {
    if (!presetDraft || !presetModuleSelection) return;
    updatePresetModules(presetModuleSelection.filter((id) => !REQUIRED_BASE_MODULE_IDS.includes(id)));
    setPresetModuleSelection(null);
  };

  const togglePresetModule = (moduleId) => {
    if (!presetDraft || REQUIRED_BASE_MODULE_IDS.includes(moduleId)) return;
    updatePresetModules(presetDraft.modules.includes(moduleId)
      ? presetDraft.modules.filter((id) => id !== moduleId)
      : [...presetDraft.modules, moduleId]);
  };

  const setPresetDefaultProfile = (hardwareProfile) => {
    if (!hardwareProfile) {
      setPresetDraft((current) => ({ ...current, defaultInstance: null }));
      return;
    }
    const instanceType = Object.keys(cloudInstanceTypesByProfile[hardwareProfile] || {})[0];
    setPresetDraft((current) => ({
      ...current,
      defaultInstance: {
        hardwareProfile,
        architecture: hardwareProfile === 'standard-arm64' ? 'arm64' : 'amd64',
        instanceType
      }
    }));
  };

  const setPresetDefaultInstanceType = (instanceType) => {
    setPresetDraft((current) => ({
      ...current,
      defaultInstance: current.defaultInstance ? { ...current.defaultInstance, instanceType } : null
    }));
  };

  const refreshPresetCatalog = async (updatedCatalog) => {
    setManifestCatalog(updatedCatalog);
    invalidateCatalogDependentCaches();
    await refreshCatalogDependentData();
  };

  const savePreset = async (event) => {
    event.preventDefault();
    if (!presetDraft || contractBusy) return;
    setPresetSaving(true);
    setCatalogRefreshing(true);
    setPresetError('');
    try {
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Sign in again to manage presets.');
      const preset = { ...presetDraft, id: presetDraft.existing ? presetDraft.id : normalizeModuleId(presetDraft.name) };
      const { existing, ...definition } = preset;
      const presetUrl = presetDraft.existing
        ? CONTROL_API.catalogPreset(definition.id)
        : CONTROL_API.catalogPresets;
      const response = await fetch(presetUrl, {
        method: presetDraft.existing ? 'PATCH' : 'POST', headers: { Authorization: idToken, 'Content-Type': 'application/json' },
        body: JSON.stringify(definition)
      });
      if (!response.ok) throw new Error(`Preset save failed (${response.status}). ${await response.text()}`);
      setPresetDraft(null);
      await refreshPresetCatalog(await response.json());
      setNodeRefreshMessage(`Saved preset ${preset.name}. No nodes were changed.`);
    } catch (saveError) {
      setPresetError(saveError.message || 'Could not save the preset.');
    } finally {
      setPresetSaving(false);
      setCatalogRefreshing(false);
    }
  };

  const deletePreset = async (preset) => {
    if (!preset || preset.id === 'base') return;
    if (!window.confirm(`Delete ${preset.name || preset.id}? This does not change deployed nodes.`)) return;
    setPresetSaving(true);
    setCatalogRefreshing(true);
    setPresetError('');
    try {
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Sign in again to manage presets.');
      const response = await fetch(CONTROL_API.catalogPreset(preset.id), {
        method: 'DELETE', headers: { Authorization: idToken }
      });
      if (!response.ok) throw new Error(`Preset deletion failed (${response.status}). ${await response.text()}`);
      await refreshPresetCatalog(await response.json());
      setNodeRefreshMessage(`Deleted preset ${preset.name || preset.id}. No nodes were changed.`);
    } catch (deleteError) {
      setPresetError(deleteError.message || 'Could not delete the preset.');
    } finally {
      setPresetSaving(false);
      setCatalogRefreshing(false);
    }
  };

  const editableModuleIdsForNode = (node) => {
    const knownModuleIds = new Set(customSelectableModules.map((moduleItem) => moduleItem.id));
    if (!node.runtimeStatusAvailable) return null;
    return (node.runtimeModules || [])
      .map((moduleItem) => moduleItem.id)
      .filter((moduleId) => knownModuleIds.has(moduleId));
  };

  const openCustomModulePicker = () => {
    if (showCreateNode) {
      // Presets choose modules; they do not own this node's overrides.  Keep
      // an image tag/settings/mount edit while the user switches to Custom.
      setCustomManifestModules((currentModules) => (
        currentModules.length ? currentModules : DEFAULT_CUSTOM_MODULE_IDS
      ));
      setModulePickerTags([]);
      setModulePickerTagsError('');
      setLoadingModulePickerTags(true);
      void fetchDefaultTagSettings()
        .then((modules) => setModulePickerTags(prepareTagSettings(modules)))
        .catch((tagError) => setModulePickerTagsError(tagError.message || 'Could not load global image tags.'))
        .finally(() => setLoadingModulePickerTags(false));
      setShowCustomModulePicker(true);
      return;
    }
    const liveModuleIds = editableModuleIdsForNode(moduleEditNode);
    if (liveModuleIds === null) {
      setModuleUpdateError('Live module status is unavailable. Reconnect the node and refresh it before editing modules.');
      return;
    }
    setCustomManifestModules([...new Set([...REQUIRED_BASE_MODULE_IDS, ...liveModuleIds])]);
    setNodeModuleOverrides(savedModuleOverrides(moduleEditNode));
    setModulePickerTags([]);
    setModulePickerTagsError('');
    setLoadingModulePickerTags(true);
    void loadModulePickerTags(moduleEditNode);
    setShowCustomModulePicker(true);
  };

  const toggleCustomManifestModule = (moduleId) => {
    setCustomManifestModules((currentModules) => (
      REQUIRED_BASE_MODULE_IDS.includes(moduleId)
        ? currentModules
        :
      currentModules.includes(moduleId)
        ? currentModules.filter((item) => item !== moduleId)
        : [...currentModules, moduleId]
    ));
  };

  const updateModulePickerTag = (moduleId, tag) => {
    setModulePickerTags((currentTags) => currentTags.map((moduleItem) => (
      moduleItem.id === moduleId ? { ...moduleItem, tag } : moduleItem
    )));
  };

  const openNodeModuleOptionEditor = (moduleId) => {
    setNodePortError('');
    const moduleItem = manifestCatalog.modules.find((item) => item.id === moduleId);
    if (!moduleItem) return;
    setCreateNodeDialog(null);
    const saved = nodeModuleOverrides[moduleId] || {};
    const tagSetting = modulePickerTags.find((item) => item.id === moduleId);
    const existingNodeSettings = moduleEditNode?.moduleSettings?.[moduleId] || {};
    const catalogueTag = catalogModuleTag(moduleItem);
    const resolvedTag = saved.tag || tagSetting?.tag || moduleEditNode?.imageTags?.[moduleId] || catalogueTag;
    setNodeModuleOptionDraft({
      moduleId,
      name: moduleItem.name || moduleId,
      tag: resolvedTag,
      manifestTag: tagSetting?.manifestTag || catalogueTag,
      settings: { ...existingNodeSettings, ...(saved.settings || {}) },
      definitions: moduleItem.settings || [],
      browserUi: saved.browserUi ?? moduleItem.browserOpenable === true,
      ...modulePortDraft(moduleItem, saved),
      mounts: saved.mounts ?? moduleItem.mounts ?? []
    });
  };

  const saveNodeModuleOptionEditor = () => {
    if (!nodeModuleOptionDraft) return;
    const { moduleId, tag, manifestTag, settings, browserUi, mounts } = nodeModuleOptionDraft;
    let ports;
    try { ports = portPayload(nodeModuleOptionDraft, browserUi); }
    catch (portError) { setNodePortError(portError.message); return; }
    setNodeModuleOverrides((current) => ({
      ...current,
      [moduleId]: {
        // A matching catalogue tag is not an override.  Keeping it empty
        // means a later global catalogue update reaches this node.
        tag: (tag || '').trim() === (manifestTag || '').trim() ? '' : (tag || '').trim(),
        settings,
        browserUi,
        ...ports,
        mounts
      }
    }));
    setNodeModuleOptionDraft(null);
  };

  const nodeOverridePayload = (overrides = nodeModuleOverrides) => {
    const imageTags = Object.fromEntries(Object.entries(overrides)
      .filter(([, value]) => value.tag)
      .map(([moduleId, value]) => [moduleId, value.tag]));
    const moduleSettings = Object.fromEntries(Object.entries(overrides)
      .filter(([, value]) => value.settings && Object.keys(value.settings).length)
      .map(([moduleId, value]) => [moduleId, value.settings]));
    const moduleRuntimeOptions = Object.fromEntries(Object.entries(overrides)
      .filter(([, value]) => value.browserUi !== undefined || value.hostNetwork !== undefined || value.hostPort !== undefined || value.containerPort !== undefined || value.exposedPort !== undefined || value.uiPort !== undefined || value.mounts !== undefined)
      .map(([moduleId, value]) => [moduleId, {
        ...(value.browserUi !== undefined ? { browserUi: value.browserUi } : {}),
        ...(value.hostNetwork !== undefined ? { hostNetwork: value.hostNetwork } : {}),
        ...((value.hostPort ?? value.exposedPort ?? value.uiPort) !== undefined && (value.hostPort ?? value.exposedPort ?? value.uiPort) !== '' ? { hostPort: Number(value.hostPort ?? value.exposedPort ?? value.uiPort) } : {}),
        ...(value.containerPort !== undefined && value.containerPort !== '' ? { containerPort: Number(value.containerPort) } : {}),
        ...(value.mounts !== undefined ? { mounts: value.mounts } : {})
      }]));
    return {
      ...(Object.keys(imageTags).length ? { imageTags } : {}),
      ...(Object.keys(moduleSettings).length ? { moduleSettings } : {}),
      ...(Object.keys(moduleRuntimeOptions).length ? { moduleRuntimeOptions } : {})
    };
  };

  const saveCustomModuleSelection = () => {
    if (moduleEditNode) {
      const liveModuleIds = editableModuleIdsForNode(moduleEditNode);
      if (liveModuleIds === null) {
        setModuleUpdateError('Live module status is unavailable. Refresh the node before saving module changes.');
        return;
      }
      const currentModuleIds = new Set(liveModuleIds);
      const addedModules = customManifestModules.filter((moduleId) => !currentModuleIds.has(moduleId));
      if (addedModules.length && !window.confirm(
        `Add ${addedModules.join(', ')} to this node's deployment?`
      )) {
        return;
      }
      const overrides = nodeOverridePayload();
      updateNodeModules(null, {
        preset: 'custom',
        modules: customManifestModules,
        imageTags: {
          ...Object.fromEntries(modulePickerTags
            .map((moduleItem) => [moduleItem.id, (moduleItem.tag || '').trim(), moduleItem.manifestTag])
            .filter(([, tag, manifestTag]) => tag && tag !== manifestTag)
            .map(([moduleId, tag]) => [moduleId, tag])),
          ...(overrides.imageTags || {})
        },
        moduleSettings: overrides.moduleSettings,
        moduleRuntimeOptions: overrides.moduleRuntimeOptions
      });
      return;
    }
    setManifestPreset('custom');
    setManifestModules(customManifestModules);
    setManifestChosenInDialog(true);
    setShowCustomModulePicker(false);
  };

  const selectManifestPreset = (presetId) => {
    if (presetId === 'custom') {
      setCloudHardwareProfile('standard-amd64');
      setCloudArchitecture('amd64');
      setCloudInstanceType('t3a.medium');
      openCustomModulePicker();
      return;
    }
    const preset = (manifestCatalog.presets || []).find((item) => item.id === presetId);
    const selectableIds = new Set(customSelectableModules.map((moduleItem) => moduleItem.id));
    setManifestPreset(presetId);
    setManifestChosenInDialog(true);
    setNodeTimeout(preset?.sessionTimeout || '4h');
    setManifestModules((preset?.modules || []).filter((moduleId) => selectableIds.has(moduleId)));
    const defaultInstance = preset?.defaultInstance;
    if (defaultInstance?.hardwareProfile && cloudInstanceTypesByProfile[defaultInstance.hardwareProfile]) {
      const instanceType = defaultInstance.instanceType && cloudInstanceTypesByProfile[defaultInstance.hardwareProfile][defaultInstance.instanceType]
        ? defaultInstance.instanceType
        : Object.keys(cloudInstanceTypesByProfile[defaultInstance.hardwareProfile])[0];
      setCloudHardwareProfile(defaultInstance.hardwareProfile);
      setCloudArchitecture(defaultInstance.architecture || (defaultInstance.hardwareProfile === 'standard-arm64' ? 'arm64' : 'amd64'));
      setCloudInstanceType(instanceType);
    } else {
      setCloudHardwareProfile('standard-amd64');
      setCloudArchitecture('amd64');
      setCloudInstanceType('t3a.medium');
    }
  };

  const openModuleEditor = (node) => {
    if (!node) {
      return;
    }

    setModuleEditNode(node);
    setManifestPreset(node.manifestPreset || 'base');
    setManifestModules(normalizeList(node.manifestModules));
    setShowCustomModulePicker(false);
    setCustomManifestModules([]);
    setModuleUpdateError('');
    loadManifestCatalog();
  };

  const updateNodeModules = async (event, selection) => {
    event?.preventDefault();
    if (!moduleEditNode) {
      return;
    }
    const selectedPreset = selection?.preset || manifestPreset;
    const selectedModules = selection?.modules || manifestModules;

    setUpdatingModules(true);
    setModuleUpdateError('');

    try {
      const idToken = await getIdToken();
      if (!idToken) {
        throw new Error('Sign in again to update this node.');
      }
      const requestBody = {
        nodeId: moduleEditNode.id,
        displayName: moduleEditNode.name || moduleEditNode.id,
        location: moduleEditNode.location || 'IoT Edge device',
        nodeType: moduleEditNode.nodeType || 'physical',
        manifestPreset: selectedPreset,
        manifestModules: selectedModules,
        ...(selection?.imageTags ? { imageTags: selection.imageTags } : {}),
        ...(selection?.moduleSettings ? { moduleSettings: selection.moduleSettings } : {}),
        ...(selection?.moduleRuntimeOptions ? { moduleRuntimeOptions: selection.moduleRuntimeOptions } : {}),
        deploy: true
      };
      const deployRequest = (body) => fetch(
        `${config.aws.apiEndpoint}/demo-nodes/${encodeURIComponent(moduleEditNode.id)}`,
        {
          method: 'PATCH',
          headers: { Authorization: idToken, 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      );
      let response = await deployRequest(requestBody);
      if (response.status === 409) {
        const conflict = await response.json().catch(() => ({}));
        if (conflict.code === 'EXISTING_DEPLOYMENT') {
          const confirmed = window.confirm('A workload is already present on this device. Purge it before deploying the selected workload?');
          if (!confirmed) return;
          response = await deployRequest({ ...requestBody, purgeExisting: true });
        }
      }

      if (!response.ok) {
        const details = await response.text();
        throw new Error(`Module update failed (${response.status}). ${details}`);
      }

      const updated = await response.json();
      const updatedNode = {
        ...moduleEditNode,
        id: updated.nodeId || moduleEditNode.id,
        name: updated.displayName || moduleEditNode.name || moduleEditNode.id,
        description: updated.description || moduleEditNode.description,
        status: updated.status === 'ONLINE' ? 'online' : 'offline',
        location: updated.location || moduleEditNode.location,
        accessMode: updated.accessMode || moduleEditNode.accessMode,
        manifestPreset: updated.manifestPreset || selectedPreset,
        manifestModules: normalizeList(updated.manifestModules || selectedModules),
        imageTags: normalizeImageTags(updated.imageTags || moduleEditNode.imageTags),
        moduleSettings: updated.moduleSettings || selection?.moduleSettings || moduleEditNode.moduleSettings || {},
        moduleRuntimeOptions: updated.moduleRuntimeOptions || selection?.moduleRuntimeOptions || moduleEditNode.moduleRuntimeOptions || {},
        deployedModules: updated.deployedModules || deriveModuleInventory({
          catalog: manifestCatalog,
          preset: updated.manifestPreset || selectedPreset,
          modules: updated.manifestModules || selectedModules
        }),
        deploymentStatus: updated.deploymentStatus || 'DEPLOYING',
        servicesReady: (updated.nodeType || moduleEditNode.nodeType) === 'ephemeral'
          ? updated.status === 'ONLINE'
          : updated.deploymentStatus === 'READY',
        allowedServices: updated.services || moduleEditNode.allowedServices || []
      };

      setNodes((currentNodes) => currentNodes.map((node) => (
        node.id === moduleEditNode.id ? updatedNode : node
      )));
      setSelectedNodeId(updatedNode.id);
      setNodeRefreshMessage(`Updated modules for ${updatedNode.name}`);
      setModuleEditNode(null);
      setManifestPreset('base');
      setManifestModules([]);
      setShowCustomModulePicker(false);
      setCustomManifestModules([]);
      setModulePickerTags([]);
      setNodeModuleOverrides({});
    } catch (updateError) {
      setModuleUpdateError(updateError.message || 'Could not update node modules.');
    } finally {
      setUpdatingModules(false);
    }
  };

  const prepareTagSettings = (modules) => sortTagSettings(modules).map((moduleItem) => {
    const catalogItem = (manifestCatalog.modules || []).find((item) => item.id === moduleItem.id) || {};
    const serviceItem = SERVICES.find((service) => service.serviceKey === catalogItem.serviceKey || service.id === moduleItem.id);
    return {
      ...moduleItem,
      description: moduleItem.description || catalogItem.description || serviceItem?.description || 'Application module used by the node deployment.'
    };
  });

  const loadModulePickerTags = async (node) => {
    try {
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Sign in again to load module image tags.');
      const response = await fetch(
        CONTROL_API.nodeModuleSettings(node.id),
        { headers: { Authorization: idToken } }
      );
      if (!response.ok) throw new Error(`Tag lookup failed (${response.status}). ${await response.text()}`);
      const result = await response.json();
      setModulePickerTags(prepareTagSettings(result.modules));
    } catch (tagError) {
      setModulePickerTagsError(tagError.message || 'Could not load module image tags.');
    } finally {
      setLoadingModulePickerTags(false);
    }
  };

  const fetchDefaultTagSettings = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && defaultTagSettingsCache.current) {
      return defaultTagSettingsCache.current;
    }
    if (defaultTagSettingsRequest.current) {
      return defaultTagSettingsRequest.current;
    }

    const request = (async () => {
      const idToken = await getIdToken();
      if (!idToken) {
        setNetworkSettingsAccess('unavailable');
        throw new Error('Sign in again to load default module tags.');
      }
      const response = await fetch(
        CONTROL_API.defaultModuleSettings,
        { headers: { Authorization: idToken } }
      );
      if (response.status === 403) {
        setNetworkSettingsAccess('denied');
        throw new Error('You do not have access to network settings.');
      }
      if (!response.ok) {
        setNetworkSettingsAccess('unavailable');
        const details = await response.text();
        throw new Error(`Default tag lookup failed (${response.status}). ${details}`);
      }
      const result = await response.json();
      if (!Array.isArray(result.modules)) {
        setNetworkSettingsAccess('unavailable');
        throw new Error('Default tag lookup returned an invalid response.');
      }
      setNetworkSettingsAccess('allowed');
      defaultTagSettingsCache.current = result.modules;
      return result.modules;
    })();

    defaultTagSettingsRequest.current = request;
    try {
      return await request;
    } finally {
      defaultTagSettingsRequest.current = null;
    }
  }, [getIdToken]);

  useEffect(() => {
    const loadInitialSettingsAccess = async () => {
      try {
        await fetchDefaultTagSettings();
      } catch {
        setNetworkSettingsAccess((currentAccess) => currentAccess === 'checking' ? 'unavailable' : currentAccess);
      } finally {
        setInitialSettingsCheckComplete(true);
      }
    };
    void loadInitialSettingsAccess();
  }, [fetchDefaultTagSettings]);

  const updateTagSetting = (moduleId, tag) => {
    if (tagSettingsNode?.id === 'default-image-tags') {
      defaultTagSettingsDirty.current = true;
    }
    setTagSettings((currentSettings) => currentSettings.map((moduleItem) => (
      moduleItem.id === moduleId ? { ...moduleItem, tag } : moduleItem
    )));
  };

  const updateModuleSetting = (moduleId, settingId, value) => {
    setModuleSettings((current) => ({
      ...current,
      [moduleId]: { ...(current[moduleId] || {}), [settingId]: value }
    }));
  };

  const updateModuleSecret = (moduleId, settingId, value) => {
    setModuleSecrets((current) => ({
      ...current,
      [moduleId]: { ...(current[moduleId] || {}), [settingId]: value }
    }));
  };

  const toggleModuleSettings = (moduleId) => {
    setExpandedModuleSettings((current) => ({ ...current, [moduleId]: !current[moduleId] }));
  };

  const fetchModuleConfiguration = async (nodeId = '', forceRefresh = false) => {
    const key = nodeId || 'global';
    if (!forceRefresh && moduleConfigCache.current.has(key)) return moduleConfigCache.current.get(key);
    if (moduleConfigRequests.current.has(key)) return moduleConfigRequests.current.get(key);
    const request = (async () => {
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Sign in again to load module settings.');
      const url = nodeId
        ? CONTROL_API.nodeModuleSettings(nodeId)
        : CONTROL_API.defaultModuleSettings;
      const response = await fetch(url, { headers: { Authorization: idToken } });
      if (!response.ok) throw new Error(`Settings lookup failed (${response.status}). ${await response.text()}`);
      const result = await response.json();
      if (!Array.isArray(result.modules) || !Array.isArray(result.settings)) throw new Error('Module configuration returned an invalid response.');
      moduleConfigCache.current.set(key, result);
      return result;
    })();
    moduleConfigRequests.current.set(key, request);
    try {
      return await request;
    } finally {
      moduleConfigRequests.current.delete(key);
    }
  };

  const saveTagSettings = async (event) => {
    event.preventDefault();
    if (!tagSettingsNode) {
      return;
    }

    setSavingTagSettings(true);
    setTagSettingsError('');
    try {
      const idToken = await getIdToken();
      if (!idToken) {
        throw new Error('Sign in again to update module tags.');
      }
      const imageTags = Object.fromEntries(tagSettings
        .map((moduleItem) => [moduleItem.id, (moduleItem.tag || '').trim(), moduleItem.manifestTag])
        .filter(([, tag, manifestTag]) => tag && tag !== manifestTag)
        .map(([moduleId, tag]) => [moduleId, tag]));
      const isDefaultTagSettings = tagSettingsNode.id === 'default-image-tags';
      const response = await fetch(
        isDefaultTagSettings
          ? CONTROL_API.defaultModuleSettings
          : CONTROL_API.nodeModuleSettings(tagSettingsNode.id),
        {
          method: 'PUT',
          headers: {
            Authorization: idToken,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(isDefaultTagSettings
            ? { imageTags, moduleSettings, moduleSecrets }
            : {
              nodeId: tagSettingsNode.id,
              displayName: tagSettingsNode.name || tagSettingsNode.id,
              location: tagSettingsNode.location || 'IoT Edge device',
              nodeType: tagSettingsNode.nodeType || 'physical',
              manifestPreset: tagSettingsNode.manifestPreset || 'base',
              manifestModules: normalizeList(tagSettingsNode.manifestModules),
              imageTags,
              moduleSettings,
              moduleSecrets,
              deploy: true
            })
        }
      );
      if (!response.ok) {
        const details = await response.text();
        throw new Error(`Tag update failed (${response.status}). ${details}`);
      }
      const updated = await response.json();
      if (isDefaultTagSettings) {
        defaultTagSettingsCache.current = tagSettings;
        moduleConfigCache.current.delete('global');
        defaultTagSettingsDirty.current = false;
        setNodeRefreshMessage('Updated default module tags for new nodes');
      } else {
        moduleConfigCache.current.delete(tagSettingsNode.id);
        const updatedImageTags = normalizeImageTags(updated.imageTags || imageTags);
        setNodes((currentNodes) => currentNodes.map((node) => (
          node.id === tagSettingsNode.id ? { ...node, imageTags: updatedImageTags, moduleSettings: updated.moduleSettings || moduleSettings } : node
        )));
        setNodeRefreshMessage(`Updated module tags for ${tagSettingsNode.name || tagSettingsNode.id}`);
      }
      setTagSettingsNode(null);
      setTagSettings([]);
    } catch (tagError) {
      setTagSettingsError(tagError.message || 'Could not update module tags.');
    } finally {
      setSavingTagSettings(false);
    }
  };

  const resetNodeModuleSettings = async () => {
    if (!tagSettingsNode || tagSettingsNode.id === 'default-image-tags') return;
    setSavingTagSettings(true);
    setTagSettingsError('');
    try {
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Sign in again to reset module settings.');
      const response = await fetch(CONTROL_API.nodeModuleSettings(tagSettingsNode.id), {
        method: 'PUT',
        headers: { Authorization: idToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nodeId: tagSettingsNode.id,
          displayName: tagSettingsNode.name || tagSettingsNode.id,
          location: tagSettingsNode.location || 'IoT Edge device',
          nodeType: tagSettingsNode.nodeType || 'physical',
          manifestPreset: tagSettingsNode.manifestPreset || 'base',
          manifestModules: normalizeList(tagSettingsNode.manifestModules),
          imageTags: tagSettingsNode.imageTags || {},
          resetModuleSettings: true,
          deploy: true
        })
      });
      if (!response.ok) throw new Error(`Reset failed (${response.status}). ${await response.text()}`);
      const updated = await response.json();
      setNodes((current) => current.map((node) => node.id === tagSettingsNode.id ? { ...node, moduleSettings: updated.moduleSettings || {} } : node));
      setTagSettingsNode(null);
      setNodeRefreshMessage(`Reset module settings for ${tagSettingsNode.name || tagSettingsNode.id}`);
    } catch (resetError) {
      setTagSettingsError(resetError.message || 'Could not reset module settings.');
    } finally {
      setSavingTagSettings(false);
    }
  };

  const openDefaultTagSettings = async () => {
    setSettingsTab('module-catalog');
    setTagSettingsNode({ id: 'default-image-tags', name: 'Catalogue settings' });
    defaultTagSettingsDirty.current = false;
    const cachedSettings = defaultTagSettingsCache.current;
    setTagSettings(cachedSettings ? prepareTagSettings(cachedSettings) : []);
    setTagSettingsError('');
    setModuleSettings({});
    setModuleSecrets({});
    setEffectiveModuleSettings({});
    setExpandedModuleSettings({});
    setModuleSettingDefinitions([]);
    setLoadingTagSettings(false);
    loadManifestCatalog();
  };

  const fetchManagedUsers = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && managedUsersCache.current) return managedUsersCache.current;
    if (managedUsersRequest.current) return managedUsersRequest.current;

    const request = (async () => {
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Sign in again to manage users.');
      const response = await fetch(CONTROL_API.adminUsers, { headers: { Authorization: idToken } });
      if (!response.ok) throw new Error(`User lookup failed (${response.status}). ${await response.text()}`);
      const result = await response.json();
      if (!Array.isArray(result.users) || !Array.isArray(result.groups)) {
        throw new Error('User management is not available yet. Deploy the updated Davax backend, then try again.');
      }
      managedUsersCache.current = result;
      return result;
    })();

    managedUsersRequest.current = request;
    try {
      return await request;
    } finally {
      managedUsersRequest.current = null;
    }
  }, [getIdToken]);

  useEffect(() => {
    if (networkSettingsAccess === 'allowed') {
      fetchManagedUsers().catch(() => {});
    }
  }, [fetchManagedUsers, networkSettingsAccess]);

  const loadManagedUsers = async (preserveSelection = false) => {
    const cachedUsers = managedUsersCache.current;
    if (cachedUsers) { setManagedUsers(cachedUsers.users); setManagedUserGroups(cachedUsers.groups); }
    setLoadingManagedUsers(!cachedUsers);
    setUserManagementError('');
    try {
      const result = await fetchManagedUsers(true);
      setManagedUsers(result.users);
      setManagedUserGroups(result.groups);
      setSelectedManagedUsernames((selected) => preserveSelection
        ? selected.filter((username) => result.users.some((managedUser) => managedUser.username === username))
        : []);
    } catch (userError) {
      setUserManagementError(userError.message || 'Could not load users.');
    } finally {
      setLoadingManagedUsers(false);
    }
  };

  const loadRegistrationPolicy = async () => {
    setLoadingRegistrationPolicy(true);
    setRegistrationPolicyError('');
    try {
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Sign in again to manage registration.');
      const response = await fetch(CONTROL_API.registrationPolicy, { headers: { Authorization: idToken } });
      if (!response.ok) throw new Error(`Registration policy lookup failed (${response.status}). ${await response.text()}`);
      const policy = await response.json();
      if (typeof policy.allowAnyEndavaEmail !== 'boolean') throw new Error('The registration policy response is invalid.');
      setAllowAnyEndavaEmail(policy.allowAnyEndavaEmail);
    } catch (policyError) {
      setAllowAnyEndavaEmail(false);
      setRegistrationPolicyError(policyError.message || 'Could not load registration policy.');
    } finally {
      setLoadingRegistrationPolicy(false);
    }
  };

  const updateRegistrationPolicy = async (enabled) => {
    const previous = allowAnyEndavaEmail;
    setAllowAnyEndavaEmail(enabled);
    setSavingRegistrationPolicy(true);
    setRegistrationPolicyError('');
    try {
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Sign in again to manage registration.');
      const response = await fetch(CONTROL_API.registrationPolicy, {
        method: 'PUT',
        headers: { Authorization: idToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ allowAnyEndavaEmail: enabled })
      });
      if (!response.ok) throw new Error(`Registration policy update failed (${response.status}). ${await response.text()}`);
      const policy = await response.json();
      if (typeof policy.allowAnyEndavaEmail !== 'boolean') throw new Error('The registration policy response is invalid.');
      setAllowAnyEndavaEmail(policy.allowAnyEndavaEmail);
    } catch (policyError) {
      setAllowAnyEndavaEmail(previous);
      setRegistrationPolicyError(policyError.message || 'Could not update registration policy.');
    } finally {
      setSavingRegistrationPolicy(false);
    }
  };

  const normalizedInviteEmail = inviteEmail.trim().toLowerCase();
  const inviteEmailValid = /^[^@\s]+@endava\.com$/.test(normalizedInviteEmail);

  const sendUserInvite = async (event) => {
    event.preventDefault();
    if (!allowAnyEndavaEmail || !inviteEmailValid || sendingUserInvite) return;
    setSendingUserInvite(true);
    setUserInviteMessage('');
    setUserManagementError('');
    try {
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Sign in again to invite users.');
      const response = await fetch(CONTROL_API.adminInvites, {
        method: 'POST',
        headers: { Authorization: idToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedInviteEmail })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || `Invitation failed (${response.status}).`);
      setInviteEmail('');
      setUserInviteMessage(result.resent
        ? `Invitation resent to ${result.email}.`
        : `Invitation sent to ${result.email}.`);
      managedUsersCache.current = null;
      await loadManagedUsers();
    } catch (inviteError) {
      setUserManagementError(inviteError.message || 'Could not send invitation.');
    } finally {
      setSendingUserInvite(false);
    }
  };

  const selectSettingsTab = (tab) => {
    setSettingsTab(tab);
    if (tab === 'user-management') {
      loadManagedUsers();
      loadRegistrationPolicy();
    }
    if (tab === 'module-catalog') {
      setCatalogError('');
      setCatalogEditorExpanded(false);
      setCatalogDraft(null);
      loadManifestCatalog();
    }
    if (tab === 'preset-editor') {
      setPresetError('');
      loadManifestCatalog();
    }
  };

  const manageSelectedUsers = async (action) => {
    const selectedUsers = managedUsers.filter((managedUser) => selectedManagedUsernames.includes(managedUser.username));
    if (!selectedUsers.length) return;
    if (!confirmUserAction(action, selectedUsers.length)) return;
    setManagingUsername('batch');
    setUserManagementError('');
    try {
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Sign in again to manage users.');
      const failures = [];
      for (const managedUser of selectedUsers) {
        const userUrl = CONTROL_API.adminUser(managedUser.username);
        const response = await fetch(userUrl, action.action === 'delete'
          ? { method: 'DELETE', headers: { Authorization: idToken } }
          : {
            method: 'PATCH',
            headers: { Authorization: idToken, 'Content-Type': 'application/json' },
            body: JSON.stringify(action.patch)
          });
        if (!response.ok) failures.push(`${managedUser.email}: ${await response.text()}`);
      }
      managedUsersCache.current = null;
      await loadManagedUsers(true);
      if (failures.length) throw new Error(`Some users could not be updated. ${failures.join(' ')}`);
    } catch (userError) {
      setUserManagementError(userError.message || 'Could not update user.');
    } finally {
      setManagingUsername('');
    }
  };

  const toggleManagedUserSelection = (username) => {
    setSelectedManagedUsernames((selected) => (
      selected.includes(username) ? selected.filter((item) => item !== username) : [...selected, username]
    ));
  };

  const loadUsers = async () => {
    if (availableUsers.length || loadingUsers) {
      return;
    }

    setLoadingUsers(true);
    setInviteError('');
    try {
      const idToken = await getIdToken();
      if (!idToken) {
        throw new Error('Sign in again to load users.');
      }
      const response = await fetch(CONTROL_API.users, {
        headers: { Authorization: idToken }
      });
      if (!response.ok) {
        const details = await response.text();
        throw new Error(`User lookup failed (${response.status}). ${details}`);
      }
      const result = await response.json();
      setAvailableUsers(result.users || []);
    } catch (lookupError) {
      setInviteError(lookupError.message || 'Could not load users.');
    } finally {
      setLoadingUsers(false);
    }
  };

  const openInvitePicker = async () => {
    setShowInvitePicker(true);
    await loadUsers();
  };

  const toggleInvitedUser = (targetUser) => {
    setInvitedUsers((currentUsers) => (
      currentUsers.some((item) => item.sub === targetUser.sub)
        ? currentUsers.filter((item) => item.sub !== targetUser.sub)
        : [...currentUsers, targetUser]
    ));
  };

  const handleNodeItemKeyDown = (event, nodeId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setSelectedNodeId(nodeId);
      setSelectedNodeGroup('');
    }
  };

  const selectNode = (nodeId) => {
    setSelectedNodeId(nodeId);
    setSelectedNodeGroup('');
  };

  const selectNodeGroup = (group) => {
    setSelectedNodeId('');
    setCollapsedNodeGroups((currentGroups) => ({
      ...currentGroups,
      [group]: !currentGroups[group]
    }));
    setSelectedNodeGroup(group);
  };

  const deleteEphemeralNode = async (node) => {
    if (node?.nodeType !== 'ephemeral') {
      return;
    }

    const confirmed = window.confirm(`Delete ${node.name || node.id}? This removes the ephemeral session and its resources.`);
    if (!confirmed) {
      return;
    }

    const deleteKey = node.id;
    setDeletingNodeId(deleteKey);
    setError('');

    try {
      const idToken = await getIdToken();
      if (!idToken) {
        throw new Error('Sign in again to delete this node.');
      }

      const response = await fetch(
        CONTROL_API.session(node.sessionId || node.id),
        {
          method: 'DELETE',
          headers: { Authorization: idToken }
        }
      );

      if (!response.ok) {
        const details = await response.text();
        throw new Error(`Node delete failed (${response.status}). ${details}`);
      }

      nodeRefreshGate.current.invalidate();
      removedNodeIds.current.add(deleteKey);
      setNodes((currentNodes) => currentNodes.filter((current) => current.id !== deleteKey));
      setSelectedNodeId((currentSelected) => (currentSelected === deleteKey ? '' : currentSelected));
      setNodeRefreshMessage(`Deleted ${node.name || node.id}`);
    } catch (deleteError) {
      setError(deleteError.message || 'Could not delete the node.');
    } finally {
      setDeletingNodeId('');
    }
  };

  const terminatePhysicalNode = async (node) => {
    if (!node || node.nodeType === 'ephemeral') {
      return;
    }

    const confirmed = window.confirm(`Detach ${node.name || node.id}? This will purge its IoT Edge workload and remove it from Davax. The IoT Edge device identity will be kept.`);
    if (!confirmed) {
      return;
    }

    const deleteKey = node.id;
    setDeletingNodeId(deleteKey);
    setError('');

    try {
      const idToken = await getIdToken();
      if (!idToken) {
        throw new Error('Sign in again to detach this node.');
      }

      const response = await fetch(
        `${config.aws.apiEndpoint}/demo-nodes/${encodeURIComponent(node.id)}?purge=true`,
        {
          method: 'DELETE',
          headers: { Authorization: idToken }
        }
      );

      if (!response.ok) {
        const details = await response.text();
        throw new Error(`Node detach failed (${response.status}). ${details}`);
      }

      const result = await response.json().catch(() => ({}));
      nodeRefreshGate.current.invalidate();
      if (response.status === 202 || result.deploymentStatus === 'PURGING') {
        setNodes((currentNodes) => currentNodes.map((current) => (
          current.id === deleteKey ? { ...current, deploymentStatus: 'PURGING', servicesReady: false } : current
        )));
        setNodeRefreshMessage(`Purging ${node.name || node.id}; it will be removed when the device confirms completion.`);
        return;
      }

      setNodes((currentNodes) => currentNodes.filter((current) => current.id !== deleteKey));
      setSelectedNodeId((currentSelected) => (currentSelected === deleteKey ? '' : currentSelected));
      removedNodeIds.current.add(deleteKey);
      setNodeRefreshMessage(`Detached ${node.name || node.id}`);
    } catch (terminateError) {
      setError(terminateError.message || 'Could not detach the node.');
    } finally {
      setDeletingNodeId('');
    }
  };

  const createNode = async (event) => {
    event.preventDefault();
    setCreatingNode(true);
    setCreateNodeError('');

    try {
      if (accessMode === 'invite' && invitedUsers.length === 0) {
        throw new Error('Select at least one invited user.');
      }
      const selectedPublicServices = publicServices.filter((serviceKey) => (
        publicServiceOptions.some((service) => service.serviceKey === serviceKey)
      ));

      const idToken = await getIdToken();
      if (!idToken) {
        throw new Error('Sign in again to create or attach a node.');
      }
      if (nodeMode === 'device') {
        const deviceId = nodeName.trim();
        const requestBody = {
          nodeId: deviceId,
          displayName: nodeDisplayName.trim() || deviceId,
          location: nodeLocation.trim() || 'Local Lab',
          nodeType: 'physical',
          manifestPreset,
          manifestModules,
          ...nodeOverridePayload(),
          publicServices: selectedPublicServices,
          accessMode,
          invitedSubs: invitedUsers.map((invitee) => invitee.sub),
          invitedEmails: invitedUsers.map((invitee) => invitee.email)
        };
        let response = await fetch(`${config.aws.apiEndpoint}/demo-nodes`, {
          method: 'POST',
          headers: {
            Authorization: idToken,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        if (response.status === 409) {
          const conflict = await response.json().catch(() => ({}));
          if (conflict.code === 'EXISTING_DEPLOYMENT' && window.confirm('A deployment is already running on this device. Purge it before deploying the selected preset?')) {
            response = await fetch(`${config.aws.apiEndpoint}/demo-nodes`, {
              method: 'POST',
              headers: { Authorization: idToken, 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...requestBody, purgeExisting: true })
            });
          } else if (conflict.code === 'EXISTING_DEPLOYMENT') {
            return;
          }
        }

        if (!response.ok) {
          const details = await response.text();
          throw new Error(`Device attach failed (${response.status}). ${details}`);
        }

        const attached = await response.json();
        const newNode = {
          id: attached.nodeId || deviceId,
          name: attached.displayName || nodeDisplayName.trim() || deviceId,
          description: attached.description || 'Registered edge node',
          status: attached.status === 'ONLINE' ? 'online' : 'offline',
          location: attached.location || nodeLocation.trim() || 'Local Lab',
          registryNode: true,
          nodeType: attached.nodeType || 'physical',
          accessMode: attached.accessMode || accessMode,
          publicServices: attached.publicServices || selectedPublicServices,
          manifestPreset: attached.manifestPreset || manifestPreset,
          manifestModules: normalizeList(attached.manifestModules || manifestModules),
          imageTags: normalizeImageTags(attached.imageTags),
          deployedModules: attached.deployedModules || deriveModuleInventory({
            catalog: manifestCatalog,
            preset: attached.manifestPreset || manifestPreset,
            modules: attached.manifestModules || manifestModules
          }),
          deploymentStatus: attached.deploymentStatus || 'DEPLOYING',
          servicesReady: attached.deploymentStatus === 'READY',
          allowedServices: attached.services || [],
          sessionId: undefined,
          instanceId: attached.instanceId,
          ssmInstanceId: attached.ssmInstanceId || attached.instanceId
        };

        nodeRefreshGate.current.invalidate();
        removedNodeIds.current.delete(newNode.id);
        setNodes((currentNodes) => [
          ...currentNodes.filter((node) => node.id !== newNode.id),
          newNode
        ]);
        setSelectedNodeId(newNode.id);
        setNodeRefreshMessage(
          newNode.deploymentStatus === 'PURGING'
            ? `Purging the existing workload on ${newNode.name}; deployment will continue automatically.`
            : `Attached ${newNode.name}`
        );
        setShowCreateNode(false);
        setNodeName('');
        setNodeDisplayName('');
        setNodeLocation('');
        setManifestPreset('base');
        setManifestModules([]);
        setNodeModuleOverrides({});
        setAccessMode('private');
        setPublicServices([]);
        setInvitedUsers([]);
        return;
      }

      // Admin-dashboard cloud nodes are disposable demo sessions. Presets
      // select a manifest only; they must not create Project records.
      const launchPayload = {
        hardwareProfile: cloudHardwareProfile,
        architecture: cloudArchitecture,
        instanceType: cloudInstanceType,
        manifestModules,
        ...nodeOverridePayload(),
        publicServices: selectedPublicServices,
        accessMode,
        invitedSubs: invitedUsers.map((invitee) => invitee.sub),
        invitedEmails: invitedUsers.map((invitee) => invitee.email),
        session: nodeName.trim(),
        manifestPreset,
        timeout: nodeTimeout
      };
      const response = await fetch(
        CONTROL_API.sessions,
        {
          method: 'POST',
          headers: {
            Authorization: idToken,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(launchPayload)
        }
      );

      if (!response.ok) {
        const details = await response.text();
        throw new Error(`Node creation failed (${response.status}). ${details}`);
      }

      const launch = await response.json();
      if (!launch.sessionId) {
        throw new Error('The demo service did not return a node ID.');
      }

      const newNode = {
        id: launch.sessionId,
        deviceId: launch.deviceId || launch.sessionId,
        name: launch.name || nodeName.trim() || launch.sessionId,
        description: 'Empty ephemeral cloud node',
        // A launch response means the session was accepted, not that the
        // instance, IoT Edge modules, and FRP route are ready yet.
        status: 'initializing',
        location: 'AWS · us-east-1',
        nodeType: 'ephemeral',
        accessMode,
        publicServices: launch.publicServices || selectedPublicServices,
        manifestPreset: launch.manifestPreset || launch.presetId || manifestPreset,
        manifestModules: normalizeList(launch.manifestModules || manifestModules),
        imageTags: normalizeImageTags(launch.imageTags),
        deployedModules: launch.deployedModules || deriveModuleInventory({
          catalog: manifestCatalog,
          preset: launch.manifestPreset || launch.presetId || manifestPreset,
          modules: launch.manifestModules || manifestModules
        }),
        sessionId: launch.sessionId,
        instanceId: launch.instanceId,
        ssmInstanceId: launch.ssmInstanceId || launch.instanceId,
        servicesReady: false,
        expiresAt: launch.expiresAt,
        projectOwner: ''
      };

      nodeRefreshGate.current.invalidate();
      removedNodeIds.current.delete(newNode.id);
      setNodes((currentNodes) => [
        ...currentNodes.filter((node) => node.id !== newNode.id),
        newNode
      ]);
      setSelectedNodeId(newNode.id);
      setNodeRefreshMessage('New node is provisioning');
      setShowCreateNode(false);
      setNodeName('');
      setNodeDisplayName('');
      setNodeLocation('');
      setManifestPreset('base');
      setNodeTimeout('4h');
      setManifestModules([]);
      setNodeModuleOverrides({});
      setAccessMode('private');
      setPublicServices([]);
      setInvitedUsers([]);
    } catch (creationError) {
      setCreateNodeError(creationError.message || 'Could not create or attach the node.');
    } finally {
      setCreatingNode(false);
    }
  };

  const launchService = async (service) => {
    if (!selectedNode) {
      return;
    }
    const launchKey = `${selectedNode.id}:${service.id}`;
    const popup = window.open('', '_blank');

    setLaunching(launchKey);
    setError('');

    if (popup) {
      popup.document.title = `Opening ${service.name}`;
      popup.document.body.innerHTML = '<p style="font:16px Arial;padding:24px;color:#142b37">Preparing secure access…</p>';
    }

    try {
      const idToken = await getIdToken();
      if (!idToken) {
        throw new Error('Your login session has expired. Sign in again to continue.');
      }

      const session = service.session(selectedNode.id);
      const response = await fetch(
        `${config.aws.apiEndpoint}/demo-token?session=${encodeURIComponent(session)}&nodeId=${encodeURIComponent(selectedNode.id)}&service=${encodeURIComponent(service.serviceKey)}&probe=true`,
        {
          headers: {
            Authorization: idToken,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const details = await response.text();
        throw new Error(`Access request failed (${response.status}). ${details}`);
      }

      const result = await response.json();
      if (!result.demoUrl) {
        throw new Error('The access service did not return a launch URL.');
      }
      if (result.routeStatus !== 'NAVIGABLE') {
        setServiceRouteStates((current) => ({
          ...current,
          [service.serviceKey]: result.routeStatus || 'UNREACHABLE'
        }));
        serviceProbeSuccesses.current.delete(service.serviceKey);
        throw new Error(`${service.name} is still becoming externally reachable. Try again shortly.`);
      }

      if (popup) {
        popup.location.replace(result.demoUrl);
      } else {
        window.location.assign(result.demoUrl);
      }
    } catch (launchError) {
      popup?.close();
      setError(launchError.message || `Could not open ${service.name}.`);
    } finally {
      setLaunching('');
    }
  };

  const copyPublicServiceLink = async (service) => {
    if (!selectedNode) return;
    try {
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Sign in again to create a public link.');
      const session = service.session(selectedNode.id);
      const response = await fetch(
        `${config.aws.apiEndpoint}/demo-token?session=${encodeURIComponent(session)}&nodeId=${encodeURIComponent(selectedNode.id)}&service=${encodeURIComponent(service.serviceKey)}`,
        { headers: { Authorization: idToken, 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error(`Public link request failed (${response.status}).`);
      const result = await response.json();
      if (!result.publicAccess || !result.demoUrl) throw new Error('This service is not configured for public links.');
      await navigator.clipboard.writeText(result.demoUrl);
      setNodeRefreshMessage(`${service.name} public link copied; it expires when this demo ends.`);
    } catch (copyError) {
      setError(copyError.message || `Could not create a public link for ${service.name}.`);
    }
  };

  return (
    <main className="demo-shell">
      {(!initialNodeLoadComplete || !initialSettingsCheckComplete) && (
        <div className="dashboard-initializing-backdrop" role="alertdialog" aria-modal="true" aria-label="Initializing dashboard">
          <section className="dashboard-initializing-dialog">
            <span className="eyebrow">Davax</span>
            <h2>Initializing dashboard…</h2>
            <p>Loading nodes and settings.</p>
          </section>
        </div>
      )}
      <header className="demo-header">
        <div className="demo-brand">
          <span className="demo-brand-mark" aria-hidden="true">D</span>
          <div>
            <div className="demo-brand-title">
              <strong>Davax{config.branchName ? ` (${config.branchName}${config.deploymentDate ? `-${localDeploymentTimestamp(config.deploymentDate, config.deploymentTime)}` : ''}${localDeploymentTimestampFromIso(backendDeploymentAt) ? ` - ${localDeploymentTimestampFromIso(backendDeploymentAt)}` : ''})` : ''}</strong>
            </div>
            <span>Demo network</span>
          </div>
        </div>

        <div className="demo-account">
          <button
            type="button"
            className={`node-default-tag-settings ${networkSettingsAccess === 'checking' || loadingTagSettings || savingTagSettings ? 'is-loading' : ''}`}
            onClick={openDefaultTagSettings}
            disabled={networkSettingsAccess !== 'allowed' || loadingTagSettings || savingTagSettings}
            aria-label="Open network settings"
            title={networkSettingsAccess === 'denied' ? 'Catalogue settings require administrator access' : networkSettingsAccess === 'checking' ? 'Checking catalogue settings access' : 'Catalogue settings'}
          >
            <svg className="settings-gear-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 6h16M4 12h16M4 18h16" />
              <circle cx="9" cy="6" r="2" />
              <circle cx="15" cy="12" r="2" />
              <circle cx="7" cy="18" r="2" />
            </svg>
          </button>
          <div>
            <span>Signed in as</span>
            <strong>{user?.email || 'Demo user'}</strong>
          </div>
          <button type="button" onClick={signOut}>Sign out</button>
        </div>
      </header>

      <div className="demo-layout">
        <aside className="node-panel" aria-label="Demo nodes">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Network</span>
              <h1>Nodes</h1>
            </div>
            <div className="node-heading-tools">
              <button
                type="button"
                className="node-add"
                onClick={openCreateNode}
                aria-label="Create empty demo node"
                title="Create empty demo node"
              >
                +
              </button>
              <button
                type="button"
                className={`node-refresh ${refreshingNodes ? 'refreshing' : ''}`}
                onClick={() => {
                  void refreshNodes({ foreground: true });
                  void loadManifestCatalog();
                }}
                disabled={refreshingNodes}
                aria-label="Refresh nodes"
                title="Refresh nodes"
              >
                <svg viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M16.5 6.5V2.8l-1.4 1.4A7 7 0 1 0 17 11" />
                  <path d="M12.8 2.8h3.7v3.7" />
                </svg>
              </button>
              <span className="node-count">{nodes.length}</span>
            </div>
          </div>

          <div className="node-list">
            {[
              { id: 'lab', label: 'Lab' },
              { id: 'cloud', label: 'Cloud' }
            ].map((group) => {
              const groupNodes = nodeGroups[group.id];
              const isCollapsed = collapsedNodeGroups[group.id];
              const isSelected = selectedNodeGroup === group.id;

              return (
                <section className={`node-group ${isCollapsed ? 'collapsed' : ''}`} key={group.id}>
                  <button
                    type="button"
                    className={`node-group-heading ${isSelected ? 'selected' : ''}`}
                    onClick={() => selectNodeGroup(group.id)}
                    aria-expanded={!isCollapsed}
                    aria-pressed={isSelected}
                  >
                    <span>{group.label}</span>
                    <span className="node-group-meta">
                      <span>{groupNodes.length}</span>
                      <span className="node-group-chevron" aria-hidden="true">⌄</span>
                    </span>
                  </button>
                  {!isCollapsed && (
                    <div className="node-group-items">
                      {groupNodes.map((node) => (
                        <div
                          role="button"
                          tabIndex={0}
                          className={`node-item ${node.id === selectedNode?.id ? 'selected' : ''}`}
                          key={node.id}
                          onClick={() => selectNode(node.id)}
                          onKeyDown={(event) => handleNodeItemKeyDown(event, node.id)}
                          aria-pressed={node.id === selectedNode?.id}
                          title={node.projectOwner ? `Project owner: ${node.projectOwner}` : undefined}
                        >
                          <span className={`status-dot ${node.status}`} aria-hidden="true" />
                          <span className="node-copy">
                            <strong>{node.name}</strong>
                            <span>{node.hardwareProfile === 'nvidia-gpu' ? 'NVIDIA GPU · AMD64' : node.architecture ? node.architecture.toUpperCase() : 'Architecture unavailable'}</span>
                          </span>
                          <span className="node-chevron" aria-hidden="true">›</span>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>

          {nodeRefreshMessage && (
            <p className="node-refresh-message" aria-live="polite">{nodeRefreshMessage}</p>
          )}

          <div className="network-summary">
            <span className="status-dot online" aria-hidden="true" />
            <div>
              <strong>Access plane online</strong>
              <span>FRP tunnel connected</span>
            </div>
          </div>
        </aside>

        <section className="service-panel">
          {!selectedNode ? (
            <div className="empty-services">
              <span aria-hidden="true">+</span>
              <h3>{nodes.length ? 'Select a node' : 'No registered nodes'}</h3>
              <p>{nodes.length ? 'Choose a node from the sidebar to view its modules.' : 'Refresh the registry or create an ephemeral demo node.'}</p>
            </div>
          ) : (
            <>
          <div className="service-heading">
            <div>
              <span className="eyebrow">Selected node</span>
              <h2>{selectedNode.name}</h2>
              <p>{selectedNode.id} · {selectedNode.location}</p>
              {selectedNode.projectOwner && <p className="selected-node-owner">Project owner: {selectedNode.projectOwner}</p>}
              <div className={`online-pill ${selectedNode.status === 'offline' ? 'offline' : selectedNode.status === 'initializing' ? 'initializing' : ''}`}>
                <span className={`status-dot ${selectedNode.status}`} aria-hidden="true" />
                {selectedNode.status === 'online' ? 'Online' : selectedNode.status === 'initializing' ? 'Initializing' : selectedNode.status}
              </div>
              {selectedNodeHasMonitor && (
                <div className={`online-pill ${monitorStreamState === 'live' ? '' : monitorStreamState === 'not-applicable' ? 'offline' : 'initializing'}`}>
                  Module status: {monitorStreamState === 'not-applicable'
                    ? 'N/A'
                    : monitorStreamState.charAt(0).toUpperCase() + monitorStreamState.slice(1)}
                </div>
              )}
              {selectedNode.nodeType !== 'ephemeral' && (
                <div className={`online-pill ${selectedNode.deploymentStatus === 'FAILED' ? 'offline' : ['DEPLOYING', 'PURGING'].includes(selectedNode.deploymentStatus) ? 'initializing' : ''}`}>
                  Deployment: {deploymentLabel(selectedNode)}
                </div>
              )}
              {selectedNode.setup && <div className="fabric-node-status" aria-label="Preset setup status" role="status">
                <strong>Setup: {selectedNode.setup.status}</strong>
                <span>{selectedNode.setup.completedSteps?.length || 0} / {selectedNode.setup.totalSteps} steps complete</span>
                {selectedNode.setup.message && <p>{selectedNode.setup.message}</p>}
              </div>}
              {selectedNode.fabric && <div className="fabric-node-status" aria-label="Fabric registration status">
                <strong>Fabric: {selectedNode.fabric.status}</strong>
                <span>{selectedNode.fabric.capabilityId} / {selectedNode.fabric.version}</span>
                {selectedNode.fabric.message && <p>{selectedNode.fabric.message}</p>}
              </div>}
            </div>
            <div className="node-header-metrics">
              {selectedNodeHasMiniTop && <MiniTopHeaderSummary snapshot={headerMiniTopSnapshot} />}
              <div className="selected-node-specs" aria-label="Node capacity">
                <span><em>Architecture</em><strong>{nodeArchitectureLabel(selectedNode)}</strong></span>
                <span><em>vCPU</em><strong>{INSTANCE_SPECS[selectedNode.instanceType]?.vcpu || '—'}</strong></span>
                <span><em>Memory</em><strong>{INSTANCE_SPECS[selectedNode.instanceType]?.memory || '—'}</strong></span>
              </div>
            </div>
            <div className="service-heading-actions">
              <button
                type="button"
                className="node-edit-modules"
                onClick={() => openModuleEditor(selectedNode)}
                disabled={updatingModules || selectedNode.deploymentStatus === 'PURGING'}
                aria-label={`Edit modules for ${selectedNode.name || selectedNode.id}`}
                title="Edit modules"
              >
                Edit
              </button>
              <button
                type="button"
                className="node-internal-toggle"
                onClick={() => setShowInternalModules((visible) => !visible)}
                aria-pressed={showInternalModules}
                aria-label={showInternalModules ? 'Hide internal modules' : 'Show internal modules'}
                title={showInternalModules ? 'Hide internal modules' : 'Show internal modules'}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  {showInternalModules ? <><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.5" /></> : <><path d="m3 3 18 18" /><path d="M10.6 6.2A10.8 10.8 0 0 1 12 6c6 0 9.5 6 9.5 6a17 17 0 0 1-3.1 3.7M6.2 6.2C3.9 7.6 2.5 12 2.5 12s3.5 6 9.5 6c1 0 1.9-.2 2.7-.5" /></>}
                </svg>
              </button>
              {selectedNode.nodeType === 'ephemeral' ? (
                <button
                  type="button"
                  className="node-delete heading-delete"
                  onClick={() => deleteEphemeralNode(selectedNode)}
                  disabled={deletingNodeId === selectedNode.id || selectedNode.deploymentStatus === 'PURGING'}
                  aria-label={`Delete ${selectedNode.name || selectedNode.id}`}
                  title="Delete ephemeral node"
                >
                  ×
                </button>
              ) : (
                <button
                  type="button"
                  className="node-delete heading-delete"
                  onClick={() => terminatePhysicalNode(selectedNode)}
                  disabled={deletingNodeId === selectedNode.id || selectedNode.deploymentStatus === 'PURGING'}
                  aria-label={`Detach ${selectedNode.name || selectedNode.id}`}
                  title="Detach node"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="launch-error" role="alert">
              <strong>Could not launch service</strong>
              <span>{error}</span>
              <button type="button" onClick={() => setError('')} aria-label="Dismiss error">×</button>
            </div>
          )}

          <div className="service-section-title">
            <h3>Modules</h3>
            <span>{visibleDeployedModules.length} listed</span>
          </div>

          {visibleDeployedModules.length === 0 ? (
            <div className="empty-services">
              <span aria-hidden="true">+</span>
              <h3>No modules deployed</h3>
              <p>This node is ready for a workload manifest.</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="service-grid">
              {visibleDeployedModules.map((moduleItem) => {
                const service = moduleItem.browserOpenable
                  ? visibleServices.find((candidate) => sameService(candidate.serviceKey, moduleItem.serviceKey))
                  : null;
                const launchKey = service ? `${selectedNode.id}:${service.id}` : '';
                const isLaunching = launching === launchKey;
                const hasRouteCandidate = Boolean(service && serviceProbeCandidates.some((candidate) => sameService(candidate.service.serviceKey, service.serviceKey)));
                const routeState = service ? serviceRouteStates[service.serviceKey] : '';
                const routeReady = !hasRouteCandidate || routeState === 'NAVIGABLE';
                const moduleState = displayRuntimeStatus(selectedNode, moduleItem);
                const moduleStateClass = moduleState.toLowerCase().replace(/[^a-z0-9]+/g, '-');

                return (
                  <article className="service-card" key={moduleItem.id}>
                    <div className="service-card-top">
                      <span className="service-mark" aria-hidden="true">
                        {(moduleItem.name || moduleItem.id).slice(0, 2).toUpperCase()}
                      </span>
                      <span className={`service-state ${moduleStateClass}`}>{moduleState}</span>
                    </div>
                    <h4>{moduleItem.name || moduleItem.id}</h4>
                    <p>{moduleItem.description || moduleItem.id}</p>
                    <div className="service-card-footer">
                      <span>{moduleItem.port ? `Port ${moduleItem.port}` : 'No port'}</span>
                      {service ? (
                        <>
                          <button
                            type="button"
                            disabled={Boolean(launching) || !routeReady}
                            onClick={() => service.id === 'mini-top' ? openMiniTopModal() : launchService(service)}
                          >
                            {service.id === 'mini-top'
                              ? 'View'
                              : isLaunching
                                ? 'Opening...'
                                : !routeReady
                                  ? routeState === 'LOCAL_NOT_READY' ? 'Waiting...' : 'Checking...'
                                  : 'Open'}
                            {!isLaunching && <span aria-hidden="true">↗</span>}
                          </button>
                          {isShareableServiceKey(service.serviceKey) && selectedNode.publicServices?.includes(service.serviceKey) && service.id !== 'mini-top' ? (
                            <button type="button" disabled={Boolean(launching) || !routeReady} onClick={() => copyPublicServiceLink(service)}>Copy public link</button>
                          ) : null}
                        </>
                      ) : (
                        <span className="module-badge">
                          {moduleItem.unexpectedRuntime ? 'Unmanaged' : moduleItem.browserOpenable ? 'Not exposed' : 'Internal'}
                        </span>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="service-table-wrap">
              <table className="service-table">
                <thead>
                  <tr>
                    <th scope="col">Module</th>
                    <th scope="col">Status</th>
                    <th scope="col">Port</th>
                    <th scope="col"><span className="visually-hidden">Open module port</span></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleDeployedModules.map((moduleItem) => {
                    const service = moduleItem.browserOpenable
                  ? visibleServices.find((candidate) => sameService(candidate.serviceKey, moduleItem.serviceKey))
                      : null;
                    const launchKey = service ? `${selectedNode.id}:${service.id}` : '';
                    const isLaunching = launching === launchKey;
                    const hasRouteCandidate = Boolean(service && serviceProbeCandidates.some((candidate) => sameService(candidate.service.serviceKey, service.serviceKey)));
                    const routeState = service ? serviceRouteStates[service.serviceKey] : '';
                    const routeReady = !hasRouteCandidate || routeState === 'NAVIGABLE';
                    const moduleState = displayRuntimeStatus(selectedNode, moduleItem);
                    const moduleStateClass = moduleState.toLowerCase().replace(/[^a-z0-9]+/g, '-');

                    return (
                      <tr key={moduleItem.id}>
                        <td>
                          <span className="service-mark table-mark" aria-hidden="true">
                            {(moduleItem.name || moduleItem.id).slice(0, 2).toUpperCase()}
                          </span>
                          <span className="table-service-copy">
                            <strong>{moduleItem.name || moduleItem.id}</strong>
                            <span>{moduleItem.id}</span>
                          </span>
                        </td>
                        <td>
                          <span className={`service-state ${moduleStateClass}`}>{moduleState}</span>
                        </td>
                        <td><span className="table-port">{moduleItem.port || '-'}</span></td>
                        <td>
                          {service ? (
                            <>
                              <button
                                type="button"
                                disabled={Boolean(launching) || !routeReady}
                                onClick={() => service.id === 'mini-top' ? openMiniTopModal() : launchService(service)}
                              >
                                {service.id === 'mini-top'
                                  ? 'View'
                                  : isLaunching
                                    ? 'Opening...'
                                    : !routeReady
                                      ? routeState === 'LOCAL_NOT_READY' ? 'Waiting...' : 'Checking...'
                                      : 'Open'}
                                {!isLaunching && <span aria-hidden="true">↗</span>}
                              </button>
                              {isShareableServiceKey(service.serviceKey) && selectedNode.publicServices?.includes(service.serviceKey) && service.id !== 'mini-top' ? (
                                <button type="button" disabled={Boolean(launching) || !routeReady} onClick={() => copyPublicServiceLink(service)}>Copy public link</button>
                              ) : null}
                            </>
                          ) : (
                            <span className="module-badge">
                              {moduleItem.unexpectedRuntime ? 'Unmanaged' : moduleItem.browserOpenable ? 'Not exposed' : 'Internal'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="service-view-footer">
            <div className="view-tools" role="group" aria-label="Module view">
              <button
                type="button"
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
                aria-pressed={viewMode === 'grid'}
                title="Grid view"
              >
                <svg viewBox="0 0 20 20" aria-hidden="true">
                  <rect x="2.5" y="2.5" width="6" height="6" />
                  <rect x="11.5" y="2.5" width="6" height="6" />
                  <rect x="2.5" y="11.5" width="6" height="6" />
                  <rect x="11.5" y="11.5" width="6" height="6" />
                </svg>
              </button>
              <button
                type="button"
                className={viewMode === 'table' ? 'active' : ''}
                onClick={() => setViewMode('table')}
                aria-label="Table view"
                aria-pressed={viewMode === 'table'}
                title="Table view"
              >
                <svg viewBox="0 0 20 20" aria-hidden="true">
                  <rect x="2.5" y="3" width="15" height="3.5" />
                  <rect x="2.5" y="8.25" width="15" height="3.5" />
                  <rect x="2.5" y="13.5" width="15" height="3.5" />
                </svg>
              </button>
            </div>
          </div>

            </>
          )}
        </section>
      </div>

      {miniTopNode && (
        <div className="node-modal-backdrop" role="presentation" onMouseDown={() => setMiniTopNode(null)}>
          <section
            className="node-modal mini-top-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mini-top-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button type="button" className="node-modal-close" onClick={() => setMiniTopNode(null)} aria-label="Close">×</button>
            <div className="mini-top-heading">
              <div>
                <h2 id="mini-top-title">System Metrics</h2>
                <span>live ({miniTopNode.name || miniTopNode.id}) · 10 sec refresh</span>
              </div>
            </div>
            <div className="mini-top-actions">
              <span>{miniTopSnapshot?.ts ? `Sample ${new Date(miniTopSnapshot.ts).toLocaleTimeString()}` : 'Waiting for sample'}</span>
            </div>
            {miniTopError && (
              <div className="node-modal-error" role="alert">{miniTopError}{miniTopSnapshot && ' Showing the last successful sample.'}</div>
            )}
            {miniTopSnapshot ? (
              <MiniTopMetrics snapshot={miniTopSnapshot} activeDetail={miniTopDetail} onSelectDetail={setMiniTopDetail} />
            ) : !miniTopError && (
              <p className="tag-settings-loading">Loading Mini Top snapshot…</p>
            )}
          </section>
        </div>
      )}

      {showCreateNode && (
        <div className="node-modal-backdrop" role="presentation" onMouseDown={cancelCreateNode}>
          <section
            className="node-modal create-node-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-node-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="node-modal-close"
              onClick={cancelCreateNode}
              disabled={creatingNode}
              aria-label="Close"
            >
              ×
            </button>
            <span className="eyebrow">Node capacity</span>
            <h2 id="create-node-title">{nodeMode === 'cloud' ? 'Create demo node' : 'Attach existing device'}</h2>
            <div className="node-mode-toggle" role="tablist" aria-label="Node action">
              <button
                type="button"
                className={nodeMode === 'cloud' ? 'active' : ''}
                onClick={() => {
                  setNodeMode('cloud');
                  setNodeName(`cloud-node-${nodes.length + 1}`);
                }}
                disabled={creatingNode}
                aria-pressed={nodeMode === 'cloud'}
              >
                Cloud demo node
              </button>
              <button
                type="button"
                className={nodeMode === 'device' ? 'active' : ''}
                onClick={() => {
                  setNodeMode('device');
                  setNodeName('');
                  setNodeDisplayName('');
                  setNodeDisplayNameEdited(false);
                  setNodeLocation('Local Lab');
                }}
                disabled={creatingNode}
                aria-pressed={nodeMode === 'device'}
              >
                Existing device
              </button>
            </div>
            <form onSubmit={createNode}>
              <label htmlFor="node-name">{nodeMode === 'cloud' ? 'Node name' : 'Device ID'}</label>
              <input
                id="node-name"
                value={nodeName}
                onChange={(event) => {
                  const deviceId = event.target.value;
                  setNodeName(deviceId);
                  if (nodeMode === 'device' && !nodeDisplayNameEdited) {
                    setNodeDisplayName(deviceId);
                  }
                }}
                placeholder={nodeMode === 'cloud' ? 'cloud-node-3' : 'baby-blue-device1'}
                pattern="[A-Za-z0-9-]+"
                maxLength="40"
                autoFocus
                required
              />
              {nodeMode === 'cloud' ? (
                <>
                  <button type="button" className="create-node-section" onClick={() => setCreateNodeDialog('hardware')} disabled={creatingNode}>
                    <span>Hardware profile</span><span>{cloudHardwareProfile} · {cloudInstanceType}</span>
                  </button>
                  {createNodeDialog === 'hardware' && <div className="node-modal-backdrop create-node-settings-backdrop" role="presentation" onMouseDown={() => setCreateNodeDialog(null)}>
                    <section className="node-modal create-node-settings-modal" role="dialog" aria-modal="true" aria-labelledby="hardware-profile-title" onMouseDown={(event) => event.stopPropagation()}>
                      <button type="button" className="node-modal-close" onClick={() => setCreateNodeDialog(null)} aria-label="Close">×</button>
                      <h2 id="hardware-profile-title">Hardware profile</h2>
                    <div className="create-node-section-content">
                      <select
                        id="cloud-hardware-profile"
                        className="create-node-architecture"
                        value={cloudHardwareProfile}
                        onChange={(event) => {
                          const hardwareProfile = event.target.value;
                          const architecture = hardwareProfile === 'standard-arm64' ? 'arm64' : 'amd64';
                          setCloudHardwareProfile(hardwareProfile);
                          setCloudArchitecture(architecture);
                          setCloudInstanceType(Object.keys(cloudInstanceTypesByProfile[hardwareProfile])[0]);
                        }}
                        disabled={creatingNode}
                        aria-label="Hardware profile"
                      >
                        <option value="standard-amd64">Standard · AMD64</option>
                        <option value="standard-arm64">Standard · ARM64</option>
                        <option value="nvidia-gpu">NVIDIA GPU · AMD64</option>
                      </select>
                      <small className="create-node-instance-help">Architecture: {cloudArchitecture.toUpperCase()}</small>
                      <fieldset className="create-node-instance-types">
                        <legend>Instance type</legend>
                        <div className="create-node-instance-grid">
                          {Object.keys(cloudInstanceTypes).map((instanceType) => (
                            <label key={instanceType} className={cloudInstanceType === instanceType ? 'selected' : ''}>
                              <input
                                type="radio"
                                name="cloud-instance-type"
                                value={instanceType}
                                checked={cloudInstanceType === instanceType}
                                onChange={(event) => setCloudInstanceType(event.target.value)}
                                disabled={creatingNode}
                              />
                              <span>{instanceType}</span>
                            </label>
                          ))}
                        </div>
                        <small className="create-node-instance-help">{cloudInstanceTypes[cloudInstanceType]}</small>
                      </fieldset>
                    </div>
                    </section>
                  </div>}
                </>
              ) : null}
              {nodeMode === 'cloud' ? (
                <>
                  <button type="button" className="create-node-section" onClick={() => setCreateNodeDialog('timeout')} disabled={creatingNode}>
                    <span>Node timeout</span><span>{SESSION_TIMEOUT_OPTIONS.find(([value]) => value === nodeTimeout)?.[1]}</span>
                  </button>
                  {createNodeDialog === 'timeout' && <div className="node-modal-backdrop create-node-settings-backdrop" role="presentation" onMouseDown={() => setCreateNodeDialog(null)}>
                    <section className="node-modal create-node-settings-modal" role="dialog" aria-modal="true" aria-labelledby="node-timeout-title" onMouseDown={(event) => event.stopPropagation()}>
                      <button type="button" className="node-modal-close" onClick={() => setCreateNodeDialog(null)} aria-label="Close">×</button>
                      <h2 id="node-timeout-title">Node timeout</h2>
                      <div className="create-node-section-content">
                    <select className="create-node-architecture" aria-label="Node timeout" value={nodeTimeout} onChange={(event) => setNodeTimeout(event.target.value)} disabled={creatingNode}>
                      {SESSION_TIMEOUT_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                    <small>Starts with the selected preset’s default. This controls the entire cloud node and cannot change after provisioning begins.</small>
                      </div>
                    </section>
                  </div>}
                </>
              ) : null}
              {nodeMode === 'device' ? (
                <>
                  <label htmlFor="node-display-name">Display name</label>
                  <input
                    id="node-display-name"
                    value={nodeDisplayName || nodeName}
                    onChange={(event) => {
                      setNodeDisplayName(event.target.value);
                      setNodeDisplayNameEdited(true);
                    }}
                    placeholder="Baby Blue"
                    maxLength="80"
                  />
                  <label htmlFor="node-location">Location</label>
                  <input
                    id="node-location"
                    value={nodeLocation}
                    onChange={(event) => setNodeLocation(event.target.value)}
                    placeholder="Local Lab"
                    maxLength="120"
                  />
                </>
              ) : null}
              <button type="button" className="create-node-section" onClick={() => { setManifestChosenInDialog(false); setCreateNodeDialog('manifest'); }} disabled={creatingNode}>
                <span>Manifest</span><span>{manifestCatalog.presets.find((preset) => preset.id === manifestPreset)?.name || manifestPreset}</span>
              </button>
              {createNodeDialog === 'manifest' && <div className="node-modal-backdrop create-node-settings-backdrop" role="presentation" onMouseDown={() => setCreateNodeDialog(null)}>
                <section className="node-modal create-node-settings-modal create-node-manifest-modal" role="dialog" aria-modal="true" aria-labelledby="manifest-title" onMouseDown={(event) => event.stopPropagation()}>
                  <button type="button" className="node-modal-close" onClick={() => setCreateNodeDialog(null)} aria-label="Close">×</button>
                  <h2 id="manifest-title">Manifest</h2>
                  <p className="manifest-explanation">A manifest defines the modules and configuration deployed to your demo node. Choose a preset below.</p>
                <div className="create-node-section-content node-manifest-field">
                  {manifestCatalogError && (
                    <p className="node-modal-error" role="alert">{manifestCatalogError}</p>
                  )}
                  <ManifestPicker presets={manifestCatalog.presets} value={manifestPreset} onChange={selectManifestPreset} disabled={creatingNode || Boolean(manifestCatalogError)} />
                  <h3 className="manifest-modules-heading">Included modules</h3>
                  <div className="node-module-summary" aria-label="Modules included in the selected manifest">
                    {manifestChosenInDialog && selectedManifestModules.length ? selectedManifestModules.map((moduleItem) => (
                      <span className="node-module-summary-item node-module-summary-editable" key={moduleItem.id}>{moduleItem.name}<button className="node-module-summary-edit" type="button" onClick={() => openNodeModuleOptionEditor(moduleItem.id)} disabled={creatingNode} aria-label={`Edit ${moduleItem.name} options`} title={`Edit ${moduleItem.name} options`}><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3.2 14.9 3.8 11l8.8-8.8a1.8 1.8 0 0 1 2.5 0l1.7 1.7a1.8 1.8 0 0 1 0 2.5L8 15.2l-3.9.6Z" /><path d="m11.3 3.5 5.2 5.2" /></svg></button></span>
                    )) : (
                      <span className="node-module-summary-empty">No modules selected.</span>
                    )}
                  </div>
                  {manifestChosenInDialog && publicServiceOptions.length ? (
                    <div className="node-public-service-links">
                      <h3 className="manifest-modules-heading">Public service links</h3>
                      <p className="manifest-explanation">Selected services get a shareable URL valid until this demo ends. Their own UI must protect private pages and write APIs.</p>
                      <div className="node-public-service-list">
                        {publicServiceOptions.map((service) => {
                          const checked = publicServices.includes(service.serviceKey);
                          return (
                            <label key={service.serviceKey} className={`node-public-service-row${checked ? ' active' : ''}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={creatingNode}
                                onChange={() => setPublicServices((current) => (
                                  checked
                                    ? current.filter((item) => item !== service.serviceKey)
                                    : [...current, service.serviceKey]
                                ))}
                              />
                              <span>{service.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
                </section>
              </div>}
              <div className="node-access-field">
                <span className="node-field-label">Access</span>
                <div className="node-access-toggle" role="radiogroup" aria-label="Node access">
                  {['private', 'public', 'invite'].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className={accessMode === mode ? 'active' : ''}
                      onClick={() => {
                        setAccessMode(mode);
                        if (mode === 'invite') {
                          openInvitePicker();
                        }
                      }}
                      disabled={creatingNode}
                      aria-pressed={accessMode === mode}
                    >
                      {mode === 'invite' ? 'By invite' : mode}
                    </button>
                  ))}
                </div>
                {accessMode === 'invite' ? (
                  <button
                    type="button"
                    className="node-invite-summary"
                    onClick={openInvitePicker}
                    disabled={creatingNode}
                  >
                    {invitedUsers.length
                      ? `${invitedUsers.length} ${invitedUsers.length === 1 ? 'user' : 'users'} selected`
                      : 'Select users'}
                  </button>
                ) : null}
              </div>
              {inviteError && <div className="node-modal-error" role="alert">{inviteError}</div>}
              {createNodeError && <div className="node-modal-error" role="alert">{createNodeError}</div>}
              <div className="node-modal-actions">
                <button type="button" onClick={cancelCreateNode} disabled={creatingNode}>
                  Cancel
                </button>
                <button type="submit" disabled={creatingNode}>
                  {creatingNode
                    ? nodeMode === 'cloud' ? 'Launching…' : 'Attaching…'
                    : nodeMode === 'cloud' ? 'Launch node' : 'Attach device'}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {moduleEditNode && (
        <div className="node-modal-backdrop" role="presentation" onMouseDown={() => !updatingModules && setModuleEditNode(null)}>
          <section
            className="node-modal node-module-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-modules-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="node-modal-close"
              onClick={() => setModuleEditNode(null)}
              disabled={updatingModules}
              aria-label="Close"
            >
              x
            </button>
            <span className="eyebrow">Workload</span>
            <h2 id="edit-modules-title">Edit modules</h2>
            <p>{moduleEditNode.name || moduleEditNode.id}</p>
            <div className="node-current-modules" aria-label="Currently deployed modules">
              {(Array.isArray(moduleEditNode.deployedModules) && moduleEditNode.deployedModules.length
                ? moduleEditNode.deployedModules
                : deriveModuleInventory({
                  catalog: manifestCatalog,
                  preset: moduleEditNode.manifestPreset || 'base',
                  modules: moduleEditNode.manifestModules || []
                })
              ).map((moduleItem) => (
                <span className="node-module-summary-item" key={moduleItem.id}>{moduleItem.name}</span>
              ))}
              <button
                type="button"
                className="node-add-module"
                onClick={openCustomModulePicker}
                disabled={updatingModules}
                aria-label="Change deployed modules"
                title="Change deployed modules"
              >
                <svg viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M13.8 2.7a1.8 1.8 0 0 1 2.5 2.5L7.1 14.4 3 15.5l1.1-4.1L13.8 2.7Z" />
                  <path d="m11.9 4.6 2.5 2.5" />
                </svg>
              </button>
            </div>
            {moduleUpdateError && <div className="node-modal-error" role="alert">{moduleUpdateError}</div>}
            <div className="node-modal-actions">
              <button type="button" onClick={() => setModuleEditNode(null)} disabled={updatingModules}>Cancel</button>
              <button
                type="button"
                onClick={() => updateNodeModules(null, {
                  preset: moduleEditNode.manifestPreset || 'base',
                  modules: normalizeList(moduleEditNode.manifestModules)
                })}
                disabled={updatingModules}
              >
                {updatingModules ? 'Deploying…' : `Redeploy ${(moduleEditNode.manifestPreset || 'base').replace(/(^|[-_\\s])(\\w)/g, (_match, prefix, letter) => `${prefix}${letter.toUpperCase()}`)}`}
              </button>
            </div>
          </section>
        </div>
      )}

      {showCustomModulePicker && (moduleEditNode || showCreateNode) && (
        <div className="node-modal-backdrop node-custom-module-backdrop" role="presentation">
          <section
            className="node-modal node-custom-module-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="custom-modules-title"
          >
            <span className="eyebrow">Custom workload</span>
            <h2 id="custom-modules-title">Choose modules</h2>
            <p>Select the modules to include for {moduleEditNode?.name || moduleEditNode?.id || nodeDisplayName || nodeName || 'this node'}.</p>
            {loadingModulePickerTags && <p className="tag-settings-loading">Loading image tags…</p>}
            <div className="node-module-list" aria-label="Available modules">
              {customSelectableModules.map((moduleItem) => {
                const checked = customManifestModules.includes(moduleItem.id);
                const required = REQUIRED_BASE_MODULE_IDS.includes(moduleItem.id);
                const tagSetting = modulePickerTags.find((item) => item.id === moduleItem.id);
                return (
                  <label className={`node-module-choice ${moduleEditNode ? 'node-module-choice-with-tag' : ''}`} key={moduleItem.id}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCustomManifestModule(moduleItem.id)}
                      disabled={updatingModules || required}
                    />
                    <span className="node-module-copy">
                      <strong>{moduleItem.name}{required ? ' (required)' : ''}</strong>
                      <small>{tagSetting?.description || moduleItem.description || 'Module description unavailable.'}</small>
                    </span>
                    {moduleEditNode && (
                      <input
                        className="node-module-tag-input"
                        value={tagSetting?.tag || ''}
                        onChange={(event) => updateModulePickerTag(moduleItem.id, event.target.value)}
                        aria-label={`Image tag for ${moduleItem.name}`}
                        placeholder={tagSetting?.manifestTag || 'Image tag'}
                        maxLength="128"
                        disabled={loadingModulePickerTags || updatingModules || !tagSetting}
                      />
                    )}
                    <button
                      type="button"
                      className="node-module-edit-options"
                      onClick={(event) => { event.preventDefault(); event.stopPropagation(); openNodeModuleOptionEditor(moduleItem.id); }}
                      disabled={!checked || loadingModulePickerTags || updatingModules}
                    >
                      Edit
                    </button>
                  </label>
                );
              })}
            </div>
            {modulePickerTagsError && <div className="node-modal-error" role="alert">{modulePickerTagsError}</div>}
            {moduleUpdateError && <div className="node-modal-error" role="alert">{moduleUpdateError}</div>}
            <div className="node-modal-actions">
              <button type="button" onClick={() => setShowCustomModulePicker(false)} disabled={updatingModules}>Cancel</button>
              <button type="button" onClick={saveCustomModuleSelection} disabled={updatingModules || (moduleEditNode && loadingModulePickerTags)}>
                {updatingModules ? 'Updating...' : moduleEditNode ? 'Save modules and tags' : 'Save selection'}
              </button>
            </div>
          </section>
        </div>
      )}

      {nodeModuleOptionDraft && (
        <div className="node-modal-backdrop catalog-option-backdrop" role="presentation" onMouseDown={() => setNodeModuleOptionDraft(null)}>
          <section className="node-modal catalog-option-modal" role="dialog" aria-modal="true" aria-labelledby="node-module-options-title" onMouseDown={(event) => event.stopPropagation()}>
            <button type="button" className="node-modal-close" onClick={() => setNodeModuleOptionDraft(null)} aria-label="Close">×</button>
            <span className="eyebrow">Node module</span>
            <h2 id="node-module-options-title">{nodeModuleOptionDraft.name}</h2>
            <p>These values apply only to this node and are used on its next deployment.</p>
            <div className="module-catalog-fields">
              <label>Image tag<input value={nodeModuleOptionDraft.tag} onChange={(event) => setNodeModuleOptionDraft((current) => ({ ...current, tag: event.target.value }))} placeholder="Image tag" maxLength="128" /></label>
              <ModulePorts value={nodeModuleOptionDraft} onChange={changes => { setNodePortError(''); setNodeModuleOptionDraft(current => ({ ...current, ...changes })); }} />
              <label className="module-catalog-external-toggle"><span>Browser UI</span><span><input type="checkbox" checked={nodeModuleOptionDraft.browserUi === true} onChange={(event) => setNodeModuleOptionDraft((current) => ({ ...current, browserUi: event.target.checked }))} /> Expose through browser access</span><small>Creates an FRP browser route to the host port.</small></label>
              {nodePortError && <div className="node-modal-error" role="alert">{nodePortError}</div>}
            </div>
            {nodeModuleOptionDraft.definitions.length > 0 && <details className="node-module-options-section"><summary>Configuration</summary><div className="node-module-options-scroll"><div className="module-catalog-fields">{nodeModuleOptionDraft.definitions.filter((setting) => setting.type !== 'secret').map((setting) => {
              const value = nodeModuleOptionDraft.settings[setting.id] ?? setting.defaultValue ?? '';
              const update = (nextValue) => setNodeModuleOptionDraft((current) => ({ ...current, settings: { ...current.settings, [setting.id]: nextValue } }));
              return <label key={setting.id} title={setting.env}><span>{setting.label}</span>{setting.type === 'boolean' ? <input type="checkbox" checked={value === true || value === 'true'} onChange={(event) => update(event.target.checked)} /> : setting.type === 'select' ? <select value={value} onChange={(event) => update(event.target.value)}>{(setting.options || []).map((option) => <option key={option} value={option}>{option}</option>)}</select> : <input type={setting.type === 'number' ? 'number' : 'text'} value={value} onChange={(event) => update(event.target.value)} maxLength={setting.maxLength} min={setting.min} max={setting.max} />}{setting.description && <small>{setting.description}</small>}</label>;
            })}</div></div></details>}
            <details className="node-module-options-section"><summary>Mount points</summary>
            <div className="node-module-options-scroll">
            <div className="module-catalog-options-heading"><span>Add mount point</span><button type="button" onClick={() => setNodeModuleOptionDraft((current) => ({ ...current, mounts: [...current.mounts, { source: '', target: '', readOnly: false }] }))}>Add mount</button></div>
            {nodeModuleOptionDraft.mounts.map((mount, index) => <div className="module-catalog-fields" key={`${index}-${mount.source}-${mount.target}`}>
              <label>Host source path<input value={mount.source} onChange={(event) => setNodeModuleOptionDraft((current) => ({ ...current, mounts: current.mounts.map((item, itemIndex) => itemIndex === index ? { ...item, source: event.target.value } : item) }))} placeholder="/var/iot/data" /></label>
              <label>Container path<input value={mount.target} onChange={(event) => setNodeModuleOptionDraft((current) => ({ ...current, mounts: current.mounts.map((item, itemIndex) => itemIndex === index ? { ...item, target: event.target.value } : item) }))} placeholder="/app/data" /></label>
              <label className="module-catalog-external-toggle"><span>Access</span><span><input type="checkbox" checked={mount.readOnly === true} onChange={(event) => setNodeModuleOptionDraft((current) => ({ ...current, mounts: current.mounts.map((item, itemIndex) => itemIndex === index ? { ...item, readOnly: event.target.checked } : item) }))} /> Read only</span></label>
              <button type="button" onClick={() => setNodeModuleOptionDraft((current) => ({ ...current, mounts: current.mounts.filter((_, itemIndex) => itemIndex !== index) }))}>Remove mount</button>
            </div>)}
            {!nodeModuleOptionDraft.mounts.length && <p className="module-catalog-note">No additional mount points.</p>}</div></details>
            <div className="node-modal-actions"><button type="button" onClick={() => setNodeModuleOptionDraft(null)}>Cancel</button><button type="button" onClick={saveNodeModuleOptionEditor}>Save module options</button></div>
          </section>
        </div>
      )}

      {tagSettingsNode && (
        <div className="node-modal-backdrop" role="presentation" onMouseDown={() => !savingTagSettings && setTagSettingsNode(null)}>
          <section
            className="node-modal node-tag-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="module-tags-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="node-modal-close"
              onClick={() => setTagSettingsNode(null)}
              disabled={savingTagSettings}
              aria-label="Close"
            >
              ×
            </button>
            <h2 id="module-tags-title">{isNodeSettingsDialog ? 'Module settings' : 'Catalogue settings'}</h2>
            {isNodeSettingsDialog && <p>{tagSettingsNode.name || tagSettingsNode.id}</p>}
            <div className="settings-layout">
              <div className="settings-tabs" role="tablist" aria-label="Settings sections">
                {!isNodeSettingsDialog && <><button type="button" role="tab" aria-selected={settingsTab === 'module-catalog'} className={settingsTab === 'module-catalog' ? 'active' : ''} onClick={() => selectSettingsTab('module-catalog')}>Module catalog</button>
                <button type="button" role="tab" aria-selected={settingsTab === 'preset-editor'} className={settingsTab === 'preset-editor' ? 'active' : ''} onClick={() => selectSettingsTab('preset-editor')}>Presets</button>
                <button type="button" role="tab" aria-selected={settingsTab === 'user-management'} className={settingsTab === 'user-management' ? 'active' : ''} onClick={() => selectSettingsTab('user-management')}>User management</button></>}
              </div>
              <div className="settings-tab-panel" role="tabpanel">
            {settingsTab === 'image-tags' ? (
              <form onSubmit={saveTagSettings}>
                <p className="tag-settings-help">
                  {tagSettingsNode.id === 'default-image-tags'
                    ? 'These defaults are copied to every new node. Existing nodes keep their own tag settings.'
                    : 'Only modules currently reported as running on this node are shown.'}
                </p>
                {loadingTagSettings ? (
                  <p className="tag-settings-loading">Loading manifest tags…</p>
                ) : (
                  <div className="node-tag-list">
                    {visibleTagSettings.map((moduleItem) => (
                      <label className="node-tag-row" key={moduleItem.id}>
                        <span className="node-tag-copy">
                          <strong>{moduleItem.name}</strong>
                          <small className="node-tag-description">{moduleItem.description || 'Application service module used by the node deployment.'}</small>
                          <small>{moduleItem.repository || moduleItem.image}</small>
                          <small>Manifest: {moduleItem.manifestTag || 'untagged'}</small>
                        </span>
                        <input value={moduleItem.tag || ''} onChange={(event) => updateTagSetting(moduleItem.id, event.target.value)} aria-label={`Image tag for ${moduleItem.name}`} maxLength="128" disabled={savingTagSettings} />
                      </label>
                    ))}
                  </div>
                )}
                {tagSettingsError && <div className="node-modal-error" role="alert">{tagSettingsError}</div>}
                <div className="node-modal-actions">
                  <button type="button" onClick={() => setTagSettingsNode(null)} disabled={savingTagSettings}>Cancel</button>
                  <button type="submit" disabled={loadingTagSettings || savingTagSettings || visibleTagSettings.length === 0}>{savingTagSettings ? 'Saving…' : tagSettingsNode.id === 'default-image-tags' ? 'Save defaults' : 'Save and deploy'}</button>
                </div>
              </form>
            ) : settingsTab === 'module-configuration' ? (
              <form onSubmit={saveTagSettings}>
                <p className="tag-settings-help">{tagSettingsNode.id === 'default-image-tags' ? 'Defaults are copied to new nodes. Existing nodes are unchanged.' : 'Saving immediately updates this node’s IoT Edge deployment. Hover a setting to see its environment-variable name.'}</p>
                {loadingTagSettings ? <p className="tag-settings-loading">Loading module configuration…</p> : (
                  <div className="node-tag-list">
                    {visibleModuleSettingDefinitions.map((moduleItem) => (
                      <div className="module-config-group" key={moduleItem.id}>
                        <button
                          type="button"
                          className="module-config-toggle"
                          onClick={() => toggleModuleSettings(moduleItem.id)}
                          aria-expanded={expandedModuleSettings[moduleItem.id] === true}
                        >
                          <span>{moduleItem.name || (manifestCatalog.modules || []).find((item) => item.id === moduleItem.id)?.name || moduleItem.id}</span>
                          <span aria-hidden="true">{expandedModuleSettings[moduleItem.id] ? '−' : '+'}</span>
                        </button>
                        {expandedModuleSettings[moduleItem.id] && <div className="module-config-settings">
                          {moduleItem.settings.length ? moduleItem.settings.map((setting) => {
                          const value = moduleSettings[moduleItem.id]?.[setting.id]
                            ?? effectiveModuleSettings[moduleItem.id]?.[setting.id]
                            ?? (setting.type === 'boolean' ? false : '');
                          return <label className="node-tag-row" key={setting.id} title={setting.env ? `Environment variable: ${setting.env}` : undefined}>
                            <span className="node-tag-copy"><strong>{setting.label}{setting.type === 'secret' || setting.scope === 'stack' ? ' (stack-specific)' : ''}{setting.required ? ' *' : ''}</strong><small className="node-tag-description">{setting.description}</small></span>
                            {setting.type === 'boolean' ? (
                              <input type="checkbox" checked={Boolean(value)} onChange={(event) => updateModuleSetting(moduleItem.id, setting.id, event.target.checked)} disabled={savingTagSettings} />
                            ) : setting.type === 'select' ? (
                              <select value={value} onChange={(event) => updateModuleSetting(moduleItem.id, setting.id, event.target.value)} disabled={savingTagSettings}>{(setting.options || []).map((option) => <option key={option} value={option}>{option}</option>)}</select>
                            ) : (
                              <input type={setting.type === 'number' ? 'number' : setting.type === 'secret' ? 'password' : 'text'} value={setting.type === 'secret' ? (moduleSecrets[moduleItem.id]?.[setting.id] || '') : value} placeholder={setting.type === 'secret' ? 'Enter a replacement secret, or leave unchanged' : ''} min={setting.min} max={setting.max} maxLength={setting.maxLength} onChange={(event) => (setting.type === 'secret' ? updateModuleSecret : updateModuleSetting)(moduleItem.id, setting.id, event.target.value)} disabled={savingTagSettings} />
                            )}
                          </label>;
                          }) : <p className="tag-settings-help">This running module has no configurable options.</p>}
                        </div>}
                      </div>
                    ))}
                    {isNodeSettingsDialog && visibleModuleSettingDefinitions.length === 0 && <p className="tag-settings-help">No running modules are currently reported for this node.</p>}
                  </div>
                )}
                {tagSettingsError && <div className="node-modal-error" role="alert">{tagSettingsError}</div>}
                <div className="node-modal-actions">
                  {tagSettingsNode.id !== 'default-image-tags' && <button type="button" onClick={resetNodeModuleSettings} disabled={savingTagSettings}>Reset to defaults</button>}
                  <button type="button" onClick={() => setTagSettingsNode(null)} disabled={savingTagSettings}>Cancel</button>
                  <button type="submit" disabled={loadingTagSettings || savingTagSettings}>{savingTagSettings ? 'Saving…' : tagSettingsNode.id === 'default-image-tags' ? 'Save defaults' : 'Save and deploy'}</button>
                </div>
              </form>
            ) : settingsTab === 'module-catalog' ? (
              <div className="module-catalog-panel">
                <p className="module-catalog-note">Definitions are saved immediately. Saving never deploys or changes a node.</p>
                <section className="module-catalog-section">
                  <div className="module-catalog-toolbar"><button className="module-catalog-add" type="button" onClick={openNewCatalogModule} disabled={catalogSaving} aria-label="Add module" title="Add module">+</button></div>
                  <CatalogFilter label="Filter modules" value={catalogFilter} onChange={setCatalogFilter} count={filteredCatalogModules.length} total={catalogModules.length} />
                  {filteredCatalogModules.length ? (
                    <div className="module-catalog-list">
                      {filteredCatalogModules.map((moduleItem) => {
                        const moduleTitle = moduleItem.name || moduleItem.id;
                        return (
                        <article
                          className="module-catalog-item module-catalog-item-editable"
                          key={moduleItem.id}
                          role="button"
                          tabIndex={catalogSaving ? -1 : 0}
                          aria-label={`Edit ${moduleTitle}`}
                          title="Select to edit"
                          aria-disabled={catalogSaving}
                          onClick={() => !catalogSaving && editCatalogModule(moduleItem)}
                          onKeyDown={(event) => {
                            if (!catalogSaving && (event.key === 'Enter' || event.key === ' ')) {
                              event.preventDefault();
                              editCatalogModule(moduleItem);
                            }
                          }}
                        >
                          <div><strong>{moduleTitle}</strong><small>{moduleItem.description || 'No description provided.'}</small></div>
                          <div className="module-catalog-actions"><button className="module-catalog-edit" type="button" onClick={(event) => { event.stopPropagation(); editCatalogModule(moduleItem); }} disabled={catalogSaving} aria-label={`Edit ${moduleTitle} module definition`}>Edit</button><button className="module-catalog-delete" type="button" onClick={(event) => { event.stopPropagation(); deleteCatalogModule(moduleItem); }} disabled={catalogSaving || REQUIRED_BASE_MODULE_IDS.includes(moduleItem.id)} aria-label={`Delete ${moduleItem.name || moduleItem.id}`} title={REQUIRED_BASE_MODULE_IDS.includes(moduleItem.id) ? 'Required module' : `Delete ${moduleItem.name || moduleItem.id}`}><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3.5 5.5h13M8 2.8h4M6.2 5.5l.7 11h6.2l.7-11M8.2 8.4v5.3M11.8 8.4v5.3" /></svg></button></div>
                        </article>
                        );
                      })}
                    </div>
                  ) : <div className="settings-empty-state">{catalogModules.length ? 'No modules match your filter.' : 'No modules have been defined.'}</div>}
                  <p className="module-catalog-note">ConfigSync and FRP Client are required. A module cannot be deleted while a preset, node, or session uses it.</p>
                </section>
                {!catalogDraft && catalogError && <div className="node-modal-error" role="alert">{catalogError}</div>}
              </div>
            ) : settingsTab === 'preset-editor' ? (
              <div className="preset-editor-panel">
                <p className="module-catalog-note">Definitions are saved immediately. Saving never deploys or changes a node.</p>
                <section className="module-catalog-section">
                  <div className="module-catalog-toolbar"><button className="module-catalog-add" type="button" onClick={() => openPresetEditor()} disabled={presetSaving} aria-label="Add preset" title="Add preset">+</button></div>
                  <CatalogFilter label="Filter presets" value={presetFilter} onChange={setPresetFilter} count={filteredCatalogPresets.length} total={catalogPresets.length} />
                  {filteredCatalogPresets.length ? (
                    <div className="module-catalog-list">
                      {filteredCatalogPresets.map((preset) => {
                        const presetTitle = preset.name || preset.id;
                        const required = preset.id === 'base';
                        return <article className="module-catalog-item module-catalog-item-editable" key={preset.id} role="button" tabIndex={presetSaving ? -1 : 0} aria-label={`Edit ${presetTitle}`} title="Select to edit" aria-disabled={presetSaving} onClick={() => !presetSaving && openPresetEditor(preset)} onKeyDown={(event) => { if (!presetSaving && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); openPresetEditor(preset); } }}>
                          <div><strong>{presetTitle}</strong><small>{(preset.modules || []).filter((id) => !REQUIRED_BASE_MODULE_IDS.includes(id)).length} optional modules</small><small>{preset.description || 'No description provided.'}</small></div>
                          <div className="module-catalog-actions"><button className="module-catalog-delete" type="button" onClick={(event) => { event.stopPropagation(); deletePreset(preset); }} disabled={presetSaving || required} aria-label={`Delete ${presetTitle}`} title={required ? 'Required preset' : `Delete ${presetTitle}`}><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3.5 5.5h13M8 2.8h4M6.2 5.5l.7 11h6.2l.7-11M8.2 8.4v5.3M11.8 8.4v5.3" /></svg></button></div>
                        </article>;
                      })}
                    </div>
                  ) : <div className="settings-empty-state">{catalogPresets.length ? 'No presets match your filter.' : 'No presets have been defined.'}</div>}
                  <p className="module-catalog-note">The Base preset is required. A preset cannot be deleted while a node or session uses it.</p>
                </section>
                {presetError && <div className="node-modal-error" role="alert">{presetError}</div>}
              </div>
            ) : (
              <div className="user-management-panel">
                <div className="user-management-heading">
                  <h3>Users</h3>
                  <div className="user-management-controls">
                    <div className="user-management-tools">
                      <input
                        type="search"
                        value={managedUserSearch}
                        onChange={(event) => setManagedUserSearch(event.target.value)}
                        placeholder="Search username or email"
                        aria-label="Search users"
                      />
                      <UserActionsSelect
                        users={managedUsers}
                        groups={managedUserGroups}
                        selectedUsernames={selectedManagedUsernames}
                        disabled={loadingManagedUsers || Boolean(managingUsername)}
                        error={userManagementError}
                        onAction={manageSelectedUsers}
                      />
                      <button type="button" onClick={() => { loadManagedUsers(); loadRegistrationPolicy(); }} disabled={loadingManagedUsers || loadingRegistrationPolicy || Boolean(managingUsername)}>Refresh</button>
                    </div>
                    <label className="registration-policy-toggle">
                      <input
                        type="checkbox"
                        checked={allowAnyEndavaEmail}
                        onChange={(event) => updateRegistrationPolicy(event.target.checked)}
                        disabled={loadingRegistrationPolicy || savingRegistrationPolicy}
                      />
                      <span>Allow all endava users</span>
                    </label>
                    <form className="user-invite-form" onSubmit={sendUserInvite}>
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(event) => { setInviteEmail(event.target.value); setUserInviteMessage(''); }}
                        placeholder="name@endava.com"
                        aria-label="Endava email to invite"
                        disabled={!allowAnyEndavaEmail || loadingRegistrationPolicy || savingRegistrationPolicy || sendingUserInvite}
                      />
                      <button
                        type="submit"
                        disabled={!allowAnyEndavaEmail || !inviteEmailValid || loadingRegistrationPolicy || savingRegistrationPolicy || sendingUserInvite}
                      >
                        {sendingUserInvite ? 'Sending…' : 'Send invite'}
                      </button>
                    </form>
                    {!allowAnyEndavaEmail && !loadingRegistrationPolicy && <small className="user-invite-hint">Enable all Endava users to send invitations.</small>}
                    {userInviteMessage && <div className="user-invite-success" role="status">{userInviteMessage}</div>}
                  </div>
                </div>
                {loadingManagedUsers ? <p className="tag-settings-loading">Loading users…</p> : managedUsers.length ? (
                  <div className="managed-user-list">
                    {filteredManagedUsers.map((managedUser) => {
                      const busy = Boolean(managingUsername);
                      return (
                        <article className={`managed-user ${managedUser.enabled ? '' : 'disabled'}`} key={managedUser.username}>
                          <label className="managed-user-select"><input type="checkbox" checked={selectedManagedUsernames.includes(managedUser.username)} onChange={() => toggleManagedUserSelection(managedUser.username)} disabled={busy} aria-label={`Select ${managedUser.email}`} /></label>
                          <div className="managed-user-copy"><strong>{managedUser.email}</strong><small>{managedUser.status}</small></div>
                          <div className="managed-user-state">
                            {!managedUser.enabled && <ManagedUserStatusIcon type="disabled" label="Disabled" />}
                            {(managedUser.groups || []).map((name) => {
                              const presentation = groupPresentation(name);
                              const description = managedUserGroups.find((group) => group.name === name)?.description;
                              return <ManagedUserStatusIcon key={name} type={presentation.icon} label={`${presentation.label} (${name})${description ? `: ${description}` : ''}`} />;
                            })}
                          </div>
                        </article>
                      );
                    })}
                    {!filteredManagedUsers.length && <div className="settings-empty-state">No users match “{managedUserSearch}”.</div>}
                  </div>
                ) : !userManagementError ? (
                  <div className="settings-empty-state">No users found in this user pool.</div>
                ) : null}
                {userManagementError && <div className="node-modal-error" role="alert">{userManagementError}</div>}
                {registrationPolicyError && <div className="node-modal-error" role="alert">{registrationPolicyError}</div>}
              </div>
            )}
              </div>
            </div>
          </section>
        </div>
      )}

      {catalogEditorExpanded && catalogDraft && <div className="node-modal-backdrop catalog-module-backdrop" role="presentation" onMouseDown={() => !catalogSaving && closeCatalogEditor()}><section className="node-modal catalog-option-modal catalog-module-editor-modal" role="dialog" aria-modal="true" aria-labelledby="catalog-module-title" onMouseDown={(event) => event.stopPropagation()}><button type="button" className="node-modal-close" onClick={closeCatalogEditor} disabled={catalogSaving} aria-label="Close">×</button><form className="module-catalog-editor" onSubmit={saveCatalogModule}>
        <h2 id="catalog-module-title">{catalogDraft.existing === true ? `Edit ${catalogDraft.name || catalogDraft.id}` : 'Add module'}</h2>
        <div className="module-catalog-fields">
          <label className="module-catalog-wide">Service Name<input value={catalogDraft.name} onChange={(event) => updateCatalogDraft('name', event.target.value)} disabled={catalogSaving} required /><small>The friendly name shown in the catalog and on the service card.</small></label>
          <label className="module-catalog-wide">Description<textarea value={catalogDraft.description} onChange={(event) => updateCatalogDraft('description', event.target.value)} disabled={catalogSaving} maxLength="500" /></label>
        </div>
        <details className="module-catalog-runtime-summary" open={catalogDraft.existing !== true}>
          <summary>Advanced deployment settings</summary>
          <div className="module-catalog-fields">
          {catalogDraft.external !== true && (catalogDraft.existing === true ? <><label>Container image<select value={catalogDraft.defaultTag || ''} onChange={(event) => updateCatalogDraft('defaultTag', event.target.value)} disabled={catalogSaving} required>
            {!new Set(availableCatalogImageTags(catalogImagesByModule[catalogDraft.id], 'tag').map((item) => item.tag)).has(catalogDraft.defaultTag) && <option value={catalogDraft.defaultTag || ''}>Current · {catalogDraft.defaultTag || 'No tag'}</option>}
            {availableCatalogImageTags(catalogImagesByModule[catalogDraft.id], catalogImageOrder).map((item) => <option key={item.tag} value={item.tag}>{item.tag}</option>)}
          </select><small>Available tags from ACR.</small>{catalogImagesErrors[catalogDraft.id] && <span className="module-catalog-image-status" role="alert">{catalogImagesErrors[catalogDraft.id]}</span>}</label><fieldset className="module-catalog-order-field"><legend>Order</legend><div className="module-catalog-radio-group"><label><input type="radio" name="catalog-image-order" value="newest" checked={catalogImageOrder === 'newest'} onChange={(event) => setCatalogImageOrder(event.target.value)} disabled={catalogSaving} /><span>Newest first</span></label><label><input type="radio" name="catalog-image-order" value="oldest" checked={catalogImageOrder === 'oldest'} onChange={(event) => setCatalogImageOrder(event.target.value)} disabled={catalogSaving} /><span>Oldest first</span></label><label><input type="radio" name="catalog-image-order" value="tag" checked={catalogImageOrder === 'tag'} onChange={(event) => setCatalogImageOrder(event.target.value)} disabled={catalogSaving} /><span>Tag A–Z</span></label></div><small>Changes only how available tags are displayed.</small></fieldset></> : <label className="module-catalog-tag-alone">Image tag<input value={catalogDraft.defaultTag || ''} onChange={(event) => updateCatalogDraft('defaultTag', event.target.value)} disabled={catalogSaving} maxLength="128" required /><small>Catalogue default used for every node without its own override.</small></label>)}
          <label>Module ID<input value={catalogDraft.id} onChange={(event) => updateCatalogDraft('id', event.target.value)} disabled={catalogSaving || catalogDraft.existing === true} pattern="[a-z][a-z0-9-]{1,62}" required /><small>Generated automatically. Override it for the deployed module ID; it is fixed after creation.</small></label>
          {catalogDraft.existing !== true && <label className="module-catalog-wide module-catalog-external-toggle"><span>Image source</span><span><input type="checkbox" checked={catalogDraft.external === true} onChange={(event) => updateCatalogDraft('external', event.target.checked)} disabled={catalogSaving} /> External registry image</span><small>Use this for images outside the standard ACR registry.</small></label>}
          {catalogDraft.external === true ? <label className="module-catalog-wide">External Image Name<input value={catalogDraft.externalImage || ''} onChange={(event) => updateCatalogDraft('externalImage', event.target.value)} disabled={catalogSaving} placeholder="docker.io/prom/prometheus:v3.0.1" required /><small>Full image reference in the format registry/image:tag. Updating it changes future deployments only.</small></label> : <label>Image Name<input value={catalogDraft.imageRepository || ''} onChange={(event) => updateCatalogDraft('imageRepository', event.target.value)} disabled={catalogSaving} placeholder="my-module" required /><small>Image path within the standard container registry. Updating it changes future deployments only.</small></label>}
          </div>
        </details>
        <article
          className="module-catalog-runtime-summary"
          role="button"
          tabIndex={catalogSaving ? -1 : 0}
          aria-label="Edit runtime options"
          aria-disabled={catalogSaving}
          onClick={() => !catalogSaving && setCatalogRuntimeOptionsOpen(true)}
          onKeyDown={(event) => {
            if (!catalogSaving && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              setCatalogRuntimeOptionsOpen(true);
            }
          }}
        >
          <h4>Runtime options</h4>
          <p>{catalogDraft.hostPort ? `Host ${catalogDraft.hostPort} → container ${catalogDraft.containerPort || '?'}. ` : 'No exposed port. '}{catalogDraft.browserOpenable ? 'Browser access enabled. ' : 'Browser access disabled. '}{(catalogDraft.mounts || []).length ? `${catalogDraft.mounts.length} additional mount point${catalogDraft.mounts.length === 1 ? '' : 's'}.` : 'No additional mount points.'}</p>
        </article>
        <p className="module-catalog-note">Docker runtime, devices, privileges, ports, mounts, and configuration defaults are edited through the module controls below. Saving changes future deployments only.</p>
        <div className="module-catalog-options-heading"><h4>Configuration options</h4><button type="button" onClick={() => openCatalogOptionEditor()} disabled={catalogSaving || catalogDraft.settings.length >= 30}>Add option</button></div>
        {catalogDraft.settings.length ? <div className="module-catalog-option-list">{catalogDraft.settings.map((setting, index) => <article className="module-catalog-option-summary module-catalog-option-summary-editable" key={`${setting.id}-${index}`} role="button" tabIndex={catalogSaving ? -1 : 0} aria-label={`Edit ${setting.label || `option ${index + 1}`}`} aria-disabled={catalogSaving} onClick={() => !catalogSaving && openCatalogOptionEditor(index)} onKeyDown={(event) => { if (!catalogSaving && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); openCatalogOptionEditor(index); } }}>
          <div><strong>{setting.label || `Option ${index + 1}`}</strong><small>{setting.id || 'No option ID'} · {setting.type} · {setting.type === 'secret' || setting.scope === 'stack' ? 'Stack-specific' : 'Shared'}{setting.required ? ' · Required' : ''}</small></div>
          <div className="module-catalog-actions"><button className="module-catalog-delete" type="button" onClick={(event) => { event.stopPropagation(); removeCatalogSetting(index); }} disabled={catalogSaving} aria-label={`Remove ${setting.label || `option ${index + 1}`}`} title={`Remove ${setting.label || `option ${index + 1}`}`}><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3.5 5.5h13M8 2.8h4M6.2 5.5l.7 11h6.2l.7-11M8.2 8.4v5.3M11.8 8.4v5.3" /></svg></button></div>
        </article>)}</div> : <p className="module-catalog-note">No configuration options have been added.</p>}
        {catalogError && <div className="node-modal-error" role="alert">{catalogError}</div>}
        <div className="node-modal-actions"><button type="button" onClick={closeCatalogEditor} disabled={catalogSaving}>Cancel</button><button type="submit" disabled={catalogSaving}>{catalogSaving ? 'Saving…' : 'Save module'}</button></div>
      </form></section></div>}

      {catalogRuntimeOptionsOpen && catalogDraft && (
        <div className="node-modal-backdrop catalog-option-backdrop" role="presentation" onMouseDown={() => !catalogSaving && setCatalogRuntimeOptionsOpen(false)}>
          <section className="node-modal catalog-option-modal" role="dialog" aria-modal="true" aria-labelledby="catalog-runtime-title" onMouseDown={(event) => event.stopPropagation()}>
            <button type="button" className="node-modal-close" onClick={() => setCatalogRuntimeOptionsOpen(false)} disabled={catalogSaving} aria-label="Close">×</button>
            <span className="eyebrow">Module runtime</span>
            <h2 id="catalog-runtime-title">Ports and mount points</h2>
            <p>These are default create options for this module. Node-specific values can override them.</p>
            <div className="module-catalog-fields">
              <ModulePorts value={catalogDraft} onChange={changes => setCatalogDraft(current => ({ ...current, ...changes }))} disabled={catalogSaving} />
              <label className="module-catalog-wide module-catalog-external-toggle"><span>Browser UI</span><span><input type="checkbox" checked={catalogDraft.browserOpenable === true} onChange={(event) => updateCatalogDraft('browserOpenable', event.target.checked)} disabled={catalogSaving} /> Expose through browser access</span><small>Creates an FRP browser route. It needs an exposed port, but an exposed port does not require browser access.</small></label>
              {catalogCreateOptions() && <><label>Container runtime<select value={catalogCreateOptions().HostConfig?.Runtime || 'default'} onChange={(event) => updateCatalogCreateOptions((options) => { const hostConfig = { ...(options.HostConfig || {}) }; if (event.target.value === 'default') delete hostConfig.Runtime; else hostConfig.Runtime = event.target.value; return { ...options, HostConfig: hostConfig }; })} disabled={catalogSaving}><option value="default">Default</option><option value="nvidia">NVIDIA</option></select></label>
              <label className="module-catalog-external-toggle"><span>Privileges</span><span><input type="checkbox" checked={catalogCreateOptions().HostConfig?.Privileged === true} onChange={(event) => updateCatalogCreateOptions((options) => ({ ...options, HostConfig: { ...(options.HostConfig || {}), Privileged: event.target.checked } }))} disabled={catalogSaving} /> Privileged container</span></label></>}
            </div>
            {catalogCreateOptions() && <div className="module-catalog-options-heading"><h4>Device mappings</h4><button type="button" onClick={() => updateCatalogCreateOptions((options) => ({ ...options, HostConfig: { ...(options.HostConfig || {}), Devices: [...(options.HostConfig?.Devices || []), { PathOnHost: '', PathInContainer: '', CgroupPermissions: 'rwm' }] } }))} disabled={catalogSaving}>Add device</button></div>}
            {(catalogCreateOptions()?.HostConfig?.Devices || []).map((device, index) => <div className="module-catalog-fields" key={`${index}-${device.PathOnHost}-${device.PathInContainer}`}><label>Host device<input value={device.PathOnHost || ''} onChange={(event) => updateCatalogCreateOptions((options) => ({ ...options, HostConfig: { ...(options.HostConfig || {}), Devices: options.HostConfig.Devices.map((item, itemIndex) => itemIndex === index ? { ...item, PathOnHost: event.target.value } : item) } }))} disabled={catalogSaving} placeholder="/dev/video0" /></label><label>Container device<input value={device.PathInContainer || ''} onChange={(event) => updateCatalogCreateOptions((options) => ({ ...options, HostConfig: { ...(options.HostConfig || {}), Devices: options.HostConfig.Devices.map((item, itemIndex) => itemIndex === index ? { ...item, PathInContainer: event.target.value } : item) } }))} disabled={catalogSaving} placeholder="/dev/video0" /></label><label>Permissions<select value={device.CgroupPermissions || 'rwm'} onChange={(event) => updateCatalogCreateOptions((options) => ({ ...options, HostConfig: { ...(options.HostConfig || {}), Devices: options.HostConfig.Devices.map((item, itemIndex) => itemIndex === index ? { ...item, CgroupPermissions: event.target.value } : item) } }))} disabled={catalogSaving}><option value="r">Read</option><option value="rw">Read/write</option><option value="rwm">Read/write/mknod</option></select></label><button className="module-catalog-remove-icon" type="button" onClick={() => updateCatalogCreateOptions((options) => ({ ...options, HostConfig: { ...(options.HostConfig || {}), Devices: options.HostConfig.Devices.filter((_, itemIndex) => itemIndex !== index) } }))} disabled={catalogSaving} aria-label="Remove device" title="Remove device"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3.5 5.5h13M8 2.8h4M6.2 5.5l.7 11h6.2l.7-11M8.2 8.4v5.3M11.8 8.4v5.3" /></svg></button></div>)}
            <div className="module-catalog-options-heading"><h4>Mount points</h4><button type="button" onClick={() => updateCatalogDraft('mounts', [...(catalogDraft.mounts || []), { source: '', target: '', readOnly: false }])} disabled={catalogSaving}>Add mount</button></div>
            {(catalogDraft.mounts || []).map((mount, index) => <div className="module-catalog-fields module-catalog-mount-fields" key={`${index}-${mount.source}-${mount.target}`}>
              <label>Host source path<input value={mount.source} onChange={(event) => updateCatalogDraft('mounts', catalogDraft.mounts.map((item, itemIndex) => itemIndex === index ? { ...item, source: event.target.value } : item))} disabled={catalogSaving} placeholder="/var/iot/data" /></label>
              <label>Container path<input value={mount.target} onChange={(event) => updateCatalogDraft('mounts', catalogDraft.mounts.map((item, itemIndex) => itemIndex === index ? { ...item, target: event.target.value } : item))} disabled={catalogSaving} placeholder="/app/data" /></label>
              <div className="module-catalog-mount-access"><label><input type="checkbox" checked={mount.readOnly === true} onChange={(event) => updateCatalogDraft('mounts', catalogDraft.mounts.map((item, itemIndex) => itemIndex === index ? { ...item, readOnly: event.target.checked } : item))} disabled={catalogSaving} /> <span>Read only</span></label><button className="module-catalog-remove-icon" type="button" onClick={() => updateCatalogDraft('mounts', catalogDraft.mounts.filter((_, itemIndex) => itemIndex !== index))} disabled={catalogSaving} aria-label="Remove mount" title="Remove mount"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3.5 5.5h13M8 2.8h4M6.2 5.5l.7 11h6.2l-.7-11M8.2 8.4v5.3M11.8 8.4v5.3" /></svg></button></div>
            </div>)}
            {!catalogDraft.mounts?.length && <p className="module-catalog-note">No additional mount points.</p>}
            <div className="node-modal-actions"><button type="button" onClick={() => setCatalogRuntimeOptionsOpen(false)} disabled={catalogSaving}>Done</button></div>
          </section>
        </div>
      )}

      {catalogOptionDraft && (
        <div className="node-modal-backdrop catalog-option-backdrop" role="presentation" onMouseDown={() => !catalogSaving && setCatalogOptionDraft(null)}>
          <section className="node-modal catalog-option-modal" role="dialog" aria-modal="true" aria-labelledby="catalog-option-title" onMouseDown={(event) => event.stopPropagation()}>
            <button type="button" className="node-modal-close" onClick={() => setCatalogOptionDraft(null)} disabled={catalogSaving} aria-label="Close">×</button>
            <span className="eyebrow">Module configuration</span>
            <h2 id="catalog-option-title">{catalogOptionDraft.index === null ? 'Add option' : 'Edit option'}</h2>
            <p>Define how this module setting is shown and saved.</p>
            <form className="catalog-option-form" onSubmit={saveCatalogOption}>
              <div className="module-catalog-fields">
                <label>Label<input value={catalogOptionDraft.setting.label} onChange={(event) => updateCatalogOptionDraft('label', event.target.value)} disabled={catalogSaving} required /></label>
                <label>Environment variable<input value={catalogOptionDraft.setting.env} onChange={(event) => updateCatalogOptionDraft('env', event.target.value.toUpperCase())} disabled={catalogSaving} pattern="[A-Z][A-Z0-9_]{0,127}" required /></label>
                <label>Type<select value={catalogOptionDraft.setting.type} onChange={(event) => updateCatalogOptionDraft('type', event.target.value)} disabled={catalogSaving}><option value="text">Text</option><option value="number">Number</option><option value="boolean">Checkbox</option><option value="select">Choice</option><option value="secret">Secret</option></select></label>
                <label>Scope<select value={catalogOptionDraft.setting.type === 'secret' ? 'stack' : (catalogOptionDraft.setting.scope || 'shared')} onChange={(event) => updateCatalogOptionDraft('scope', event.target.value)} disabled={catalogSaving || catalogOptionDraft.setting.type === 'secret'}><option value="shared">Shared default across stacks</option><option value="stack">Stack-specific — configure separately</option></select></label>
                <label><input type="checkbox" checked={catalogOptionDraft.setting.required === true} onChange={(event) => updateCatalogOptionDraft('required', event.target.checked)} disabled={catalogSaving} />Required before module launch</label>
                <label className="module-catalog-wide">Description<textarea value={catalogOptionDraft.setting.description || ''} onChange={(event) => updateCatalogOptionDraft('description', event.target.value)} disabled={catalogSaving} maxLength="500" /></label>
                {catalogOptionDraft.setting.scope !== 'stack' && catalogOptionDraft.setting.type !== 'secret' && catalogOptionDraft.setting.type !== 'boolean' && <label>Default value<input value={catalogOptionDraft.setting.defaultValue ?? ''} onChange={(event) => updateCatalogOptionDraft('defaultValue', event.target.value)} disabled={catalogSaving} /></label>}
                {catalogOptionDraft.setting.scope !== 'stack' && catalogOptionDraft.setting.type === 'boolean' && <label>Default value<select value={catalogOptionDraft.setting.defaultValue ?? ''} onChange={(event) => updateCatalogOptionDraft('defaultValue', event.target.value)} disabled={catalogSaving}><option value="">No default</option><option value="true">Checked</option><option value="false">Unchecked</option></select></label>}
                {catalogOptionDraft.setting.type === 'select' && <label>Choices<input value={catalogOptionDraft.setting.optionsText || ''} onChange={(event) => updateCatalogOptionDraft('optionsText', event.target.value)} disabled={catalogSaving} placeholder="one, two, three" required /></label>}
                {catalogOptionDraft.setting.type === 'number' && <><label>Minimum<input type="number" value={catalogOptionDraft.setting.min ?? ''} onChange={(event) => updateCatalogOptionDraft('min', event.target.value)} disabled={catalogSaving} required /></label><label>Maximum<input type="number" value={catalogOptionDraft.setting.max ?? ''} onChange={(event) => updateCatalogOptionDraft('max', event.target.value)} disabled={catalogSaving} required /></label></>}
                {catalogOptionDraft.setting.type === 'text' && <label>Maximum length<input type="number" min="1" max="4096" value={catalogOptionDraft.setting.maxLength ?? ''} onChange={(event) => updateCatalogOptionDraft('maxLength', event.target.value)} disabled={catalogSaving} /></label>}
              </div>
              <div className="node-modal-actions"><button type="button" onClick={() => setCatalogOptionDraft(null)} disabled={catalogSaving}>Cancel</button><button type="submit" disabled={catalogSaving}>Save option</button></div>
            </form>
          </section>
        </div>
      )}

      {presetDraft && (
        <div className="node-modal-backdrop preset-modal-backdrop" role="presentation" onMouseDown={() => !presetSaving && !contractBusy && setPresetDraft(null)}>
          <section className="node-modal preset-editor-modal" role="dialog" aria-modal="true" aria-labelledby="preset-editor-title" onMouseDown={(event) => event.stopPropagation()}>
            <button type="button" className="node-modal-close" onClick={() => setPresetDraft(null)} disabled={presetSaving || contractBusy} aria-label="Close">×</button>
            <h2 id="preset-editor-title">{presetDraft.existing ? 'Edit preset' : 'Add preset'}</h2>
            <form className="preset-editor-form" onSubmit={savePreset}>
              <label>Preset name<input value={presetDraft.name} onChange={(event) => setPresetDraft((current) => ({ ...current, name: event.target.value }))} disabled={presetSaving} required autoFocus /></label>
              <label>Description<textarea value={presetDraft.description || ''} onChange={(event) => setPresetDraft((current) => ({ ...current, description: event.target.value }))} disabled={presetSaving} maxLength="500" /></label>
              <label>Node timeout<select value={presetDraft.sessionTimeout || '4h'} onChange={(event) => setPresetDraft((current) => ({ ...current, sessionTimeout: event.target.value }))} disabled={presetSaving}>{SESSION_TIMEOUT_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select><small>Set when the node starts. The launch can choose a different timeout.</small></label>
              <label>Hardware profile<select value={presetDraft.defaultInstance?.hardwareProfile || ''} onChange={(event) => setPresetDefaultProfile(event.target.value)} disabled={presetSaving}><option value="">Choose at launch</option>{Object.keys(cloudInstanceTypesByProfile).map((profile) => <option key={profile} value={profile}>{profile}</option>)}</select><small>The launch can choose a different hardware profile.</small></label>
              {presetDraft.defaultInstance?.hardwareProfile && <label>Instance type<select value={presetDraft.defaultInstance.instanceType} onChange={(event) => setPresetDefaultInstanceType(event.target.value)} disabled={presetSaving}>{Object.keys(cloudInstanceTypesByProfile[presetDraft.defaultInstance.hardwareProfile] || {}).map((instanceType) => <option key={instanceType} value={instanceType}>{instanceType}</option>)}</select><small>Architecture: {presetDraft.defaultInstance.architecture.toUpperCase()}</small></label>}
              <div className="module-catalog-options-heading"><h3>Modules</h3><button className="preset-module-edit" type="button" onClick={openPresetModulePicker} disabled={presetSaving || contractBusy}>Edit modules</button></div>
              <div className="preset-module-list preset-selected-module-list" aria-label="Modules included in this preset">
                {selectedPresetModules.map((moduleItem) => <div className="preset-module-row" key={moduleItem.id}>
                  <span className="preset-module-copy"><strong>{moduleItem.name || moduleItem.id}</strong><small>{moduleItem.description || moduleItem.id}</small></span>
                  {REQUIRED_BASE_MODULE_IDS.includes(moduleItem.id)
                    ? <span className="preset-module-required">Required</span>
                    : <button className="preset-module-edit" type="button" onClick={() => togglePresetModule(moduleItem.id)} disabled={presetSaving || contractBusy} aria-label={`Remove ${moduleItem.name || moduleItem.id} from preset`}>Remove</button>}
                </div>)}
              </div>
              {presetModuleNotice && <p className="module-catalog-note" role="status">{presetModuleNotice}</p>}
              <label>Automatic setup<select value={presetDraft.setupProfile ? `${presetDraft.setupProfile.id}@${presetDraft.setupProfile.version}` : ''}
                onChange={event => {
                  const profile = (manifestCatalog.setupProfiles || []).find(profile => `${profile.id}@${profile.version}` === event.target.value);
                  setPresetDraft(current => ({ ...current, setupProfile: profile ? { id: profile.id, version: profile.version } : null }));
                }} disabled={presetSaving}>
                <option value="">None</option>
                {(manifestCatalog.setupProfiles || []).map(profile => <option key={`${profile.id}@${profile.version}`} value={`${profile.id}@${profile.version}`}>
                  {profile.name} ({profile.version})
                </option>)}
              </select><small>Runs after deployment. Required modules and Fabric access policy are checked before launch.</small></label>
              <FabricContractEditor contracts={manifestCatalog.contracts || []} value={presetDraft.fabricContract}
                onChange={fabricContract => { setPresetModuleNotice(''); setPresetDraft(current => ({ ...current, fabricContract })); }}
                onCatalogChange={setManifestCatalog} api={CONTROL_API} getIdToken={getIdToken} disabled={presetSaving}
                onBusyChange={setContractBusy} available={presetDraft.modules.includes('solar-1') && presetDraft.modules.includes('fabric-agent')} />
              {presetError && <div className="node-modal-error" role="alert">{presetError}</div>}
              <div className="node-modal-actions"><button type="button" onClick={() => setPresetDraft(null)} disabled={presetSaving || contractBusy}>Cancel</button><button type="submit" disabled={presetSaving || contractBusy}>{presetSaving ? 'Saving…' : 'Save preset'}</button></div>
            </form>
          </section>
        </div>
      )}

      {presetDraft && presetModuleSelection && (
        <div className="node-modal-backdrop preset-module-picker-backdrop" role="presentation" onMouseDown={() => setPresetModuleSelection(null)}>
          <section className="node-modal preset-module-picker-modal" role="dialog" aria-modal="true" aria-labelledby="preset-module-picker-title" onMouseDown={(event) => event.stopPropagation()}>
            <button type="button" className="node-modal-close" onClick={() => setPresetModuleSelection(null)} aria-label="Close module selection">×</button>
            <h2 id="preset-module-picker-title">Edit preset modules</h2>
            <p>{presetDraft.name || 'New preset'} · Choose which modules to include. Save the preset to keep your changes.</p>
            <CatalogFilter label="Filter modules" value={presetModuleFilter} onChange={setPresetModuleFilter} count={filteredPresetModuleChoices.length} total={catalogModules.length} />
            <div className="preset-module-choice-list">
              {filteredPresetModuleChoices.map((moduleItem) => {
                const required = REQUIRED_BASE_MODULE_IDS.includes(moduleItem.id);
                return <label className="preset-module-selection preset-module-choice" key={moduleItem.id}>
                  <input type="checkbox" checked={required || presetModuleSelection.includes(moduleItem.id)} disabled={required} onChange={() => togglePresetModuleChoice(moduleItem.id)} />
                  <span><strong>{moduleItem.name || moduleItem.id}{required ? ' (required)' : ''}</strong><small>{moduleItem.description || moduleItem.id}</small></span>
                </label>;
              })}
              {filteredPresetModuleChoices.length === 0 && <p className="module-catalog-note">No modules match your filter.</p>}
            </div>
            <div className="node-modal-actions"><button type="button" onClick={() => setPresetModuleSelection(null)}>Cancel</button><button type="button" onClick={savePresetModuleSelection}>Save selection</button></div>
          </section>
        </div>
      )}

      {catalogRefreshing && (
        <div className="catalog-refresh-backdrop" role="alertdialog" aria-modal="true" aria-label="Updating catalog">
          <section className="catalog-refresh-dialog">
            <span className="eyebrow">Module catalog</span>
            <h2>Updating catalog…</h2>
            <p>Refreshing tags and configuration options.</p>
          </section>
        </div>
      )}

      {showInvitePicker && (
        <div className="node-modal-backdrop" role="presentation" onMouseDown={() => setShowInvitePicker(false)}>
          <section
            className="node-modal invite-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="invite-users-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="node-modal-close"
              onClick={() => setShowInvitePicker(false)}
              aria-label="Close"
            >
              x
            </button>
            <span className="eyebrow">Invites</span>
            <h2 id="invite-users-title">Select users</h2>
            {loadingUsers ? (
              <div className="invite-loading">Loading users...</div>
            ) : (
              <div className="invite-list">
                {availableUsers.map((availableUser) => {
                  const checked = invitedUsers.some((item) => item.sub === availableUser.sub);
                  return (
                    <label className="invite-user" key={availableUser.sub}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleInvitedUser(availableUser)}
                      />
                      <span>
                        <strong>{availableUser.email}</strong>
                        <small>{availableUser.username}</small>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            {inviteError && <div className="node-modal-error" role="alert">{inviteError}</div>}
            <div className="node-modal-actions">
              <button type="button" onClick={() => setShowInvitePicker(false)}>
                Done
              </button>
            </div>
          </section>
        </div>
      )}

    </main>
  );
};

export default Dashboard;
